// src/pages/components/dialogs/ClaimWizardDialog.jsx
// ─────────────────────────────────────────────────────────────────────────────
//  OPTIMIZED FLOW — v4
//  ✅ Green premium UI (dark gradient header, green cards, success screen)
//  ✅ borderRadius: 0 everywhere (no rounded corners)
//  ✅ Fixed-header + scrollable-body + fixed-footer layout (mobile-friendly)
//  ✅ All API endpoints + payload structures UNCHANGED from v3
//  ✅ wizState machine: "form" | "loading" | "success"
// ─────────────────────────────────────────────────────────────────────────────

import React, {
  useEffect, useRef, useState, useMemo, useContext, useCallback,
} from "react";
import {
  Dialog, Box, Typography, Stack, Button, Divider,
  TextField, RadioGroup, Radio, Chip, CircularProgress,
  Alert, LinearProgress, FormControlLabel,
} from "@mui/material";
import CheckCircleRoundedIcon    from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedIcon  from "@mui/icons-material/RadioButtonUnchecked";
import HomeRoundedIcon           from "@mui/icons-material/HomeRounded";
import StoreRoundedIcon          from "@mui/icons-material/StoreRounded";
import ShieldRoundedIcon         from "@mui/icons-material/ShieldRounded";
import CloseRoundedIcon          from "@mui/icons-material/CloseRounded";
import CameraAltRoundedIcon      from "@mui/icons-material/CameraAltRounded";
import LocalShippingRoundedIcon  from "@mui/icons-material/LocalShippingRounded";
import ArrowForwardRoundedIcon   from "@mui/icons-material/ArrowForwardRounded";
import axios from "axios";
import { BASE_URL }    from "../../config/api";
import { AuthContext } from "../../context/AuthContext";

// ─── Design tokens ────────────────────────────────────────────────────────────
const GREEN        = "#15803d";
const GREEN_LIGHT  = "#16a34a";
const GREEN_ACCENT = "#22c55e";
const GREEN_PALE   = "#dcfce7";
const DARK         = "#0a1628";
const DARK2        = "#0f3d20";
const INK          = "#111827";
const INK_2        = "#374151";
const INK_3        = "#6b7280";
const INK_4        = "#9ca3af";
const BORDER       = "#e5e7eb";
const GRAY_50      = "#f9fafb";

// ─── Constants ────────────────────────────────────────────────────────────────
const STEPS = [
  "Select Device",
  "Issue Details",
  "Upload Photos",
  "Service Location",
  "Review & Submit",
];
const STEP_ICONS = ["📱", "🔧", "📷", "📍", "✅"];
const MIN_PHOTOS    = 1;
const MAX_PHOTOS    = 6;
const MAX_FILE_MB   = 10;
const ALLOWED_TYPES = [
  "image/jpeg", "image/jpg", "image/png",
  "image/webp", "image/heic", "image/heif",
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const normalizeAddr = (a) => {
  if (!a) return null;
  const line1  = a.address_line1 || a.address1 || a.street || "";
  const city   = a.city  || "";
  const state  = a.state || "";
  const pin    = a.pincode || a.pin_code || a.zip || "";
  const label  = a.label  || a.name || a.tag || "";
  const display = [line1, city, state, pin].filter(Boolean).join(", ");
  return { ...a, _line1: line1, _city: city, _state: state, _pin: pin, _label: label, _display: display };
};

const getBrandEmoji = (name = "") => {
  const n = name.toLowerCase();
  if (n.includes("apple") || n.includes("iphone")) return "🍎";
  if (n.includes("samsung"))  return "📱";
  if (n.includes("oneplus"))  return "🔴";
  if (n.includes("xiaomi") || n.includes("redmi") || n.includes("mi")) return "🟡";
  if (n.includes("oppo"))     return "🟢";
  if (n.includes("vivo"))     return "🔵";
  if (n.includes("realme"))   return "🟠";
  if (n.includes("nokia"))    return "🔷";
  if (n.includes("motorola")) return "〽️";
  if (n.includes("google") || n.includes("pixel")) return "🌈";
  return "📱";
};

const maskImei = (imei = "") =>
  imei.length >= 8 ? `${imei.slice(0, 4)}${"•".repeat(imei.length - 8)}${imei.slice(-4)}` : imei;

// ─── Sub-components ───────────────────────────────────────────────────────────
const SummarySection = ({ title, onEdit, editStep, setStep, children }) => (
  <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 0, overflow: "hidden", mb: 1.5 }}>
    <Box sx={{
      px: 2, py: 1.2,
      background: `linear-gradient(90deg, ${DARK} 0%, ${DARK2} 100%)`,
      display: "flex", justifyContent: "space-between", alignItems: "center",
    }}>
      <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>{title}</Typography>
      <Button size="small"
        onClick={() => setStep(editStep)}
        sx={{
          color: GREEN_ACCENT, fontWeight: 700, fontSize: 11,
          textTransform: "none", borderRadius: 0, p: "2px 8px",
          border: `1px solid ${GREEN_ACCENT}`,
          "&:hover": { bgcolor: "rgba(34,197,94,0.08)" },
        }}>
        Edit
      </Button>
    </Box>
    <Box sx={{ px: 2, py: 1.5, bgcolor: "#fff" }}>{children}</Box>
  </Box>
);

const InfoBox = ({ children, color = INK_3 }) => (
  <Box sx={{
    border: `1px solid ${GREEN_PALE}`,
    bgcolor: "#f0fdf4",
    px: 2, py: 1.2,
    borderRadius: 0,
    borderLeft: `3px solid ${GREEN_ACCENT}`,
  }}>
    <Typography variant="caption" sx={{ color, lineHeight: 1.6 }}>{children}</Typography>
  </Box>
);

const StepHeading = ({ icon, title, subtitle }) => (
  <Box sx={{ mb: 2 }}>
    <Stack direction="row" alignItems="center" spacing={1} mb={0.3}>
      {icon && <Box sx={{ fontSize: 20 }}>{icon}</Box>}
      <Typography sx={{ fontWeight: 800, fontSize: 16, color: INK }}>{title}</Typography>
    </Stack>
    {subtitle && (
      <Typography variant="caption" sx={{ color: INK_3, pl: icon ? "28px" : 0 }}>{subtitle}</Typography>
    )}
  </Box>
);

const TrustBadges = () => (
  <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
    {[
      { icon: "🔒", label: "Secure" },
      { icon: "⚡", label: "Fast Process" },
      { icon: "🛡", label: "Genuine Parts" },
    ].map((b) => (
      <Box key={b.label} sx={{
        display: "flex", alignItems: "center", gap: 0.5,
        px: 1.5, py: 0.5,
        border: `1px solid ${GREEN_PALE}`,
        bgcolor: "#f0fdf4",
        borderRadius: 0,
      }}>
        <Typography fontSize={11}>{b.icon}</Typography>
        <Typography sx={{ fontSize: 10, fontWeight: 600, color: GREEN }}>{b.label}</Typography>
      </Box>
    ))}
  </Stack>
);

// ─────────────────────────────────────────────────────────────────────────────
export default function ClaimWizardDialog({
  open,
  onClose,
  devices          = [],
  addresses        = [],
  channelPartners  = [],
  retailerInfo     = null,
  onSubmit,
  preselectedDeviceId = "",
  submitting: externalSubmitting = false,
}) {
  const { user } = useContext(AuthContext);

  // Normalised lists
  const normalizedDevices = useMemo(() => devices.map((d) => ({
    ...d,
    productId:   d.product?.id,
    displayName: d.product_name || "Device",
    displayPlan: `${d.brand_name || ""} ${d.model || ""}`.trim(),
  })), [devices]);

  const normalizedAddresses = useMemo(() => addresses.map(normalizeAddr), [addresses]);

  // ── Core state ────────────────────────────────────────────────────────────
  const [step,         setStep]         = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError,  setSubmitError]  = useState("");
  const [wizState,     setWizState]     = useState("form"); // "form"|"loading"|"success"
  const [claimRef,     setClaimRef]     = useState("");

  const [draft, setDraft] = useState({
    device_id: "", issues: [], notes: "", photos: [],
    service: "pickup", pickup_address_id: "", drop_partner_id: "",
  });

  // ── Issues ────────────────────────────────────────────────────────────────
  const [issues,        setIssues]        = useState([]);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [issuesError,   setIssuesError]   = useState("");

  // ── Photos ────────────────────────────────────────────────────────────────
  const [uploadingPhotos, setUploadingPhotos] = useState([]);
  const [photoError,      setPhotoError]      = useState("");
  const [previewPhoto,    setPreviewPhoto]     = useState(null);
  const photoPreviews = useRef([]);

  // ── Drop-off ──────────────────────────────────────────────────────────────
  const [defaultRetailer,  setDefaultRetailer]  = useState(null);
  const [selectedRetailer, setSelectedRetailer] = useState(null);
  const [retailerPincode,  setRetailerPincode]  = useState("");
  const [retailerResults,  setRetailerResults]  = useState([]);
  const [retailerLoading,  setRetailerLoading]  = useState(false);
  const [showManualDrop,   setShowManualDrop]   = useState(false);
  const [manualRetailer,   setManualRetailer]   = useState({
    business_name: "", address_line1: "", city: "", state: "", pincode: "",
  });

  // ── Validation ────────────────────────────────────────────────────────────
  const [errors, setErrors] = useState({});

  // ── Derived ───────────────────────────────────────────────────────────────
  const activeRetailer = selectedRetailer || defaultRetailer;
  const submittingAny  = isSubmitting || externalSubmitting;
  const selectedDevice = normalizedDevices.find((d) => d.id === draft.device_id) || null;
  const pickupAddress  = normalizedAddresses.find((a) => a.id === draft.pickup_address_id) || null;

  // ── Init on open ──────────────────────────────────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return;
    const firstAddrId = addresses?.[0]?.id || "";
    setStep(preselectedDeviceId ? 1 : 0);
    setIsSubmitting(false);
    setSubmitError("");
    setWizState("form");
    setClaimRef("");
    setDraft({ device_id: preselectedDeviceId || "", issues: [], notes: "", photos: [], service: "pickup", pickup_address_id: firstAddrId, drop_partner_id: "" });
    setIssues([]);
    setIssuesError("");
    setUploadingPhotos([]);
    setPhotoError("");
    setPreviewPhoto(null);
    setRetailerPincode("");
    setRetailerResults([]);
    setSelectedRetailer(null);
    setDefaultRetailer(null);
    setShowManualDrop(false);
    setManualRetailer({ business_name: "", address_line1: "", city: "", state: "", pincode: "" });
    setErrors({});

    if (preselectedDeviceId) {
      const dev = devices.find((d) => d.id === preselectedDeviceId);
      if (dev?.product?.id) fetchIssuesByProduct(dev.product.id);
    }
    if (!preselectedDeviceId && devices.length === 1) {
      const dev = devices[0];
      setDraft((p) => ({ ...p, device_id: dev.id }));
      if (dev?.product?.id) { fetchIssuesByProduct(dev.product.id); setStep(1); }
    }
  }, [open]);

  // ── Set default retailer ──────────────────────────────────────────────────
  useEffect(() => {
    if (draft.service === "drop" && retailerInfo) {
      setDefaultRetailer(retailerInfo);
      setSelectedRetailer(null);
    }
  }, [draft.service, retailerInfo]);

  // ── Cleanup ───────────────────────────────────────────────────────────────
  useEffect(() => {
    return () => { try { photoPreviews.current.forEach((u) => URL.revokeObjectURL(u)); } catch {} };
  }, []);

  // ── Navigation ────────────────────────────────────────────────────────────
  const next = useCallback(() => setStep((s) => Math.min(STEPS.length - 1, s + 1)), []);
  const back = useCallback(() => { setErrors({}); setStep((s) => Math.max(0, s - 1)); }, []);

  // ── Validation ────────────────────────────────────────────────────────────
  const validateStep = (s) => {
    const errs = {};
    if (s === 0 && !draft.device_id)
      errs.device = "Please select a device to continue.";
    if (s === 1) {
      if (issuesLoading)              errs.issues = "Issues are still loading — please wait.";
      else if (!draft.issues.length)  errs.issues = "Please select at least one issue.";
    }
    if (s === 2) {
      const busy = uploadingPhotos.some((p) => p.status === "uploading");
      if (draft.photos.length < MIN_PHOTOS) errs.photos = `Please upload at least ${MIN_PHOTOS} photo.`;
      else if (busy)                         errs.photos = "Please wait for uploads to finish.";
    }
    if (s === 3) {
      if (draft.service === "pickup" && !draft.pickup_address_id)
        errs.location = "Please select a pickup address.";
      if (draft.service === "drop") {
        if (activeRetailer?.id) { /* ok */ }
        else if (showManualDrop) errs.location = "Please confirm your manual location.";
        else                     errs.location = "Please select or enter a drop-off location.";
      }
    }
    setErrors(errs);
    return !Object.keys(errs).length;
  };

  const handleNext = () => { if (validateStep(step)) next(); };

  // ── Fetch coverages ───────────────────────────────────────────────────────
  const fetchIssuesByProduct = async (productId) => {
    if (!productId) return;
    try {
      setIssues([]); setIssuesLoading(true); setIssuesError("");
      const res  = await axios.get(`${BASE_URL}warranty/coverages/3`, { params: { product_id: productId } });
      const list = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
      setIssues(list);
      if (!list.length) setIssuesError("No issues available for this product.");
    } catch {
      setIssuesError("Failed to load issues. Tap Retry.");
    } finally {
      setIssuesLoading(false);
    }
  };

  // ── Device select ─────────────────────────────────────────────────────────
  const handleSelectDevice = (device) => {
    
    console.log("Selected device:", device);
    if (!device.product_id) { console.error("Product ID missing:", device); return; }
    setDraft((p) => ({ ...p, device_id: device.id, issues: [] }));
    setErrors({});
    fetchIssuesByProduct(device.productId);
    setTimeout(next, 250);
  };

  // ── Toggle issue ──────────────────────────────────────────────────────────
  const toggleIssue = (issue) =>
    setDraft((prev) => ({
      ...prev,
      issues: prev.issues.some((i) => i.id === issue.id)
        ? prev.issues.filter((i) => i.id !== issue.id)
        : [...prev.issues, { id: issue.id, title: issue.title }],
    }));

  // ── Image compression (unchanged) ─────────────────────────────────────────
  const compressImage = (file, { maxWidth = 1280, maxHeight = 1280, quality = 0.7 } = {}) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        let { width, height } = img;
        if (width > maxWidth || height > maxHeight) {
          const s = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * s); height = Math.round(height * s);
        }
        const c = document.createElement("canvas");
        c.width = width; c.height = height;
        c.getContext("2d").drawImage(img, 0, 0, width, height);
        c.toBlob((blob) => {
          URL.revokeObjectURL(url);
          if (!blob) { reject(new Error("Compression failed")); return; }
          resolve(new File([blob], file.name, { type: "image/jpeg", lastModified: Date.now() }));
        }, "image/jpeg", quality);
      };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Load failed")); };
      img.src = url;
    });

  // ── Photo upload ──────────────────────────────────────────────────────────
  const uploadPhoto = async (file) => {
    if (!ALLOWED_TYPES.includes(file.type?.toLowerCase())) {
      setPhotoError(`"${file.name}" is not a supported format.`); return;
    }
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      setPhotoError(`"${file.name}" exceeds ${MAX_FILE_MB} MB.`); return;
    }
    const active = uploadingPhotos.filter((p) => p.status !== "error").length;
    if (draft.photos.length + active >= MAX_PHOTOS) {
      setPhotoError(`Maximum ${MAX_PHOTOS} photos allowed.`); return;
    }
    setPhotoError("");
    const tempId = `${file.name}-${Date.now()}`;
    setUploadingPhotos((prev) => [...prev, { tempId, name: file.name, status: "uploading", progress: 0, photo_id: null, url: null }]);

    try {
      const compressed = await compressImage(file);
      const fd = new FormData();
      fd.append("w_customer_id", user?.id);
      fd.append("photo_type", "front");
      fd.append("photo", compressed);
      const res = await axios.post(`${BASE_URL}warranty/claim/upload-photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: ({ loaded, total }) => {
          const pct = Math.round((loaded / total) * 100);
          setUploadingPhotos((prev) => prev.map((p) => p.tempId === tempId ? { ...p, progress: pct } : p));
        },
      });
      if (!res.data?.status) throw new Error("Upload failed");
      setUploadingPhotos((prev) => prev.map((p) => p.tempId === tempId
        ? { ...p, status: "success", progress: 100, photo_id: res.data.photo_id, url: res.data.photo_url }
        : p
      ));
      setDraft((prev) => ({ ...prev, photos: [...prev.photos, { photo_id: res.data.photo_id, photo_url: res.data.photo_url }] }));
    } catch {
      setUploadingPhotos((prev) => prev.map((p) => p.tempId === tempId ? { ...p, status: "error", progress: 0 } : p));
    }
  };

  const handleAddPhotos = (files) => Array.from(files || []).forEach(uploadPhoto);

  const removePhoto = (tempId) => {
    const photo = uploadingPhotos.find((p) => p.tempId === tempId);
    setUploadingPhotos((prev) => prev.filter((p) => p.tempId !== tempId));
    if (photo?.photo_id) setDraft((prev) => ({ ...prev, photos: prev.photos.filter((p) => p.photo_id !== photo.photo_id) }));
  };

  // ── Retailers by pincode ──────────────────────────────────────────────────
  const fetchRetailersByPincode = async () => {
    if (retailerPincode.length !== 6) return;
    try {
      setRetailerLoading(true); setRetailerResults([]);
      const res = await axios.post(`${BASE_URL}warranty/retailers/by-pincode`, { pincode: retailerPincode });
      setRetailerResults(res.data?.status ? (res.data.data || []) : []);
    } catch { setRetailerResults([]); }
    finally { setRetailerLoading(false); }
  };

  // ── Manual retailer confirm ───────────────────────────────────────────────
  const confirmManualRetailer = () => {
    const { business_name, city, pincode } = manualRetailer;
    if (!business_name?.trim() || !city?.trim() || !pincode?.trim()) return;
    setSelectedRetailer({ id: `manual_${Date.now()}`, ...manualRetailer, _isManual: true });
    setShowManualDrop(false);
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const raiseClaim = async () => {
    for (let i = 0; i < STEPS.length - 1; i++) {
      if (!validateStep(i)) { setStep(i); return; }
    }
    const payload = {
      w_customer_id:     user.id,
      w_device_id:       draft.device_id,
      claim_type:        draft.service,
      issue_description: draft.issues.map((i) => i.title).join(", "),
      coverage_ids:      draft.issues.map((i) => i.id),
      photo_ids:         draft.photos.map((p) => p.photo_id),
      ...(draft.service === "pickup" && { pickup_address_id: draft.pickup_address_id || 0 }),
      ...(draft.service === "drop" && !activeRetailer?._isManual && { drop_retailer_id: activeRetailer?.id }),
    };
    try {
      setIsSubmitting(true);
      setWizState("loading");
      setSubmitError("");
      const res = await axios.post(`${BASE_URL}warranty/claim/raise`, payload);
      if (res.data?.status) {
        const ref = `GX-CLM-${Math.floor(Math.random() * 9000000 + 1000000)}`;
        setClaimRef(ref);
        setWizState("success");
        onSubmit?.();
      } else {
        throw new Error(res.data?.message || "Claim failed");
      }
    } catch (err) {
      setSubmitError(err?.response?.data?.message || err.message || "Failed to raise claim.");
      setWizState("form");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Reset ─────────────────────────────────────────────────────────────────
  const resetForm = useCallback(() => {
    setStep(0); setIsSubmitting(false); setSubmitError("");
    setWizState("form"); setClaimRef("");
    setDraft({ device_id: "", issues: [], notes: "", photos: [], service: "pickup", pickup_address_id: addresses?.[0]?.id || "", drop_partner_id: "" });
    setIssues([]); setIssuesError(""); setErrors({});
    setUploadingPhotos([]); setPhotoError(""); setPreviewPhoto(null);
    setRetailerPincode(""); setRetailerResults([]); setSelectedRetailer(null); setDefaultRetailer(null);
    setShowManualDrop(false); setManualRetailer({ business_name: "", address_line1: "", city: "", state: "", pincode: "" });
  }, [addresses]);

  // ─── Progress bar width ────────────────────────────────────────────────────
  const progressPct = ((step + 1) / STEPS.length) * 100;

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={!submittingAny && wizState !== "loading" ? onClose : null}
      fullWidth
      PaperProps={{
        sx: {
          m: "8px",
          width: "calc(100% - 16px)",
          maxHeight: "92dvh",
          borderRadius: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          bgcolor: GRAY_50,
        },
      }}
    >

      {/* ══════════════════════════════════════════════════════════════════════
          FIXED HEADER — dark gradient with step circles
      ══════════════════════════════════════════════════════════════════════ */}
      {wizState !== "success" && (
        <Box sx={{
          flexShrink: 0,
          background: `linear-gradient(135deg, ${DARK} 0%, ${DARK2} 60%, ${GREEN} 100%)`,
          px: 2.5,
          pt: 2,
          pb: 0,
        }}>
          {/* Title row */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={0.3}>
                <ShieldRoundedIcon sx={{ color: GREEN_ACCENT, fontSize: 20 }} />
                <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 16, letterSpacing: 0.3 }}>
                  Raise a Claim
                </Typography>
              </Stack>
              <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: 11 }}>
                Step {step + 1} of {STEPS.length} — {STEPS[step]}
              </Typography>
            </Box>
            <Box
              onClick={!submittingAny ? () => { resetForm(); onClose(); } : undefined}
              sx={{
                width: 32, height: 32,
                display: "flex", alignItems: "center", justifyContent: "center",
                bgcolor: "rgba(255,255,255,0.1)",
                cursor: submittingAny ? "default" : "pointer",
                borderRadius: 0,
                "&:hover": { bgcolor: "rgba(255,255,255,0.18)" },
              }}
            >
              <CloseRoundedIcon sx={{ color: "#fff", fontSize: 18 }} />
            </Box>
          </Stack>

          {/* Step circles */}
          <Stack direction="row" alignItems="center" sx={{ mb: 0 }}>
            {STEPS.map((label, idx) => {
              const done    = idx < step;
              const current = idx === step;
              return (
                <React.Fragment key={label}>
                  {/* Step circle */}
                  <Stack alignItems="center" sx={{ minWidth: 36 }}>
                    <Box sx={{
                      width: 30, height: 30,
                      borderRadius: 0,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontWeight: 700, fontSize: done ? 16 : 12,
                      bgcolor: done ? GREEN_ACCENT : current ? "#fff" : "rgba(255,255,255,0.12)",
                      color:   done ? "#fff" : current ? GREEN : "rgba(255,255,255,0.5)",
                      border:  current ? `2px solid ${GREEN_ACCENT}` : "2px solid transparent",
                      transition: "all 250ms ease",
                      flexShrink: 0,
                    }}>
                      {done ? "✓" : STEP_ICONS[idx]}
                    </Box>
                    <Typography sx={{
                      fontSize: 8, mt: 0.3,
                      color: current ? GREEN_ACCENT : done ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.35)",
                      fontWeight: current ? 700 : 400,
                      textAlign: "center",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      maxWidth: 40,
                      textOverflow: "ellipsis",
                    }}>
                      {label}
                    </Typography>
                  </Stack>

                  {/* Connector line (not after last) */}
                  {idx < STEPS.length - 1 && (
                    <Box sx={{
                      flex: 1, height: 2,
                      bgcolor: idx < step ? GREEN_ACCENT : "rgba(255,255,255,0.15)",
                      mx: 0.3,
                      mb: 1.5,
                      transition: "background-color 300ms ease",
                    }} />
                  )}
                </React.Fragment>
              );
            })}
          </Stack>

          {/* Progress bar */}
          <LinearProgress
            variant="determinate"
            value={progressPct}
            sx={{
              height: 3,
              borderRadius: 0,
              bgcolor: "rgba(255,255,255,0.1)",
              "& .MuiLinearProgress-bar": {
                background: `linear-gradient(90deg, ${GREEN_ACCENT}, ${GREEN_LIGHT})`,
                borderRadius: 0,
              },
            }}
          />
        </Box>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          SUCCESS SCREEN
      ══════════════════════════════════════════════════════════════════════ */}
      {wizState === "success" && (
        <Box sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          background: `linear-gradient(160deg, ${DARK} 0%, ${DARK2} 50%, ${GREEN} 100%)`,
          minHeight: 400,
        }}>
          {/* Shield checkmark */}
          <Box sx={{
            width: 80, height: 80,
            bgcolor: "rgba(34,197,94,0.15)",
            border: `2px solid ${GREEN_ACCENT}`,
            borderRadius: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            mb: 2.5,
          }}>
            <ShieldRoundedIcon sx={{ fontSize: 44, color: GREEN_ACCENT }} />
          </Box>

          <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 20, mb: 0.5, textAlign: "center" }}>
            Claim Submitted!
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: 13, mb: 2.5, textAlign: "center" }}>
            Our team will contact you within 24 hours.
          </Typography>

          {/* Claim reference card */}
          <Box sx={{
            border: `1px solid rgba(34,197,94,0.4)`,
            bgcolor: "rgba(34,197,94,0.08)",
            px: 3, py: 2,
            mb: 3, width: "100%",
            maxWidth: 320,
            textAlign: "center",
          }}>
            <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 11, mb: 0.3 }}>
              CLAIM REFERENCE
            </Typography>
            <Typography sx={{ color: GREEN_ACCENT, fontWeight: 800, fontSize: 20, letterSpacing: 1.5 }}>
              {claimRef}
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: 10, mt: 0.5 }}>
              Save this for tracking your claim
            </Typography>
          </Box>

          {/* Summary pills */}
          <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" mb={3} useFlexGap>
            <Box sx={{ px: 1.5, py: 0.5, border: "1px solid rgba(34,197,94,0.3)", borderRadius: 0 }}>
              <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: 11 }}>
                📱 {selectedDevice?.displayPlan || "Device"}
              </Typography>
            </Box>
            <Box sx={{ px: 1.5, py: 0.5, border: "1px solid rgba(34,197,94,0.3)", borderRadius: 0 }}>
              <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: 11 }}>
                {draft.service === "pickup" ? "🚚 Pickup" : "🏪 Drop-off"}
              </Typography>
            </Box>
            <Box sx={{ px: 1.5, py: 0.5, border: "1px solid rgba(34,197,94,0.3)", borderRadius: 0 }}>
              <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: 11 }}>
                🔧 {draft.issues.length} issue{draft.issues.length !== 1 ? "s" : ""}
              </Typography>
            </Box>
          </Stack>

          <Button
            fullWidth
            onClick={() => { resetForm(); onClose(); }}
            sx={{
              background: `linear-gradient(90deg, ${GREEN} 0%, ${GREEN_ACCENT} 100%)`,
              color: "#fff",
              fontWeight: 700,
              fontSize: 14,
              py: 1.5,
              borderRadius: 0,
              textTransform: "none",
              maxWidth: 320,
              "&:hover": { background: `linear-gradient(90deg, ${GREEN_LIGHT}, ${GREEN_ACCENT})` },
            }}
          >
            Done ✓
          </Button>
        </Box>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          LOADING OVERLAY
      ══════════════════════════════════════════════════════════════════════ */}
      {wizState === "loading" && (
        <Box sx={{
          flex: 1,
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          background: `linear-gradient(160deg, ${DARK} 0%, ${DARK2} 100%)`,
          p: 4,
          minHeight: 300,
        }}>
          <CircularProgress sx={{ color: GREEN_ACCENT, mb: 2 }} size={48} thickness={3} />
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>Submitting your claim…</Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: 12, mt: 0.5 }}>Please don't close this window</Typography>
        </Box>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          SCROLLABLE BODY (form steps)
      ══════════════════════════════════════════════════════════════════════ */}
      {wizState === "form" && (
        <Box sx={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          px: 2.5,
          py: 2,
          bgcolor: GRAY_50,
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-thumb": { bgcolor: "#d1d5db", borderRadius: 0 },
        }}>

          {/* ──────────────────────────────────────────────────────────────────
              STEP 0 — SELECT DEVICE
          ────────────────────────────────────────────────────────────────── */}
          {step === 0 && (
            <Stack spacing={1.5}>
              <StepHeading icon="📱" title="Select Your Device" subtitle="Choose the device you want to raise a claim for" />

              {normalizedDevices.length === 0 && (
                <Alert severity="info" sx={{ borderRadius: 0, fontSize: 13 }}>
                  No registered devices found. Please contact your retailer.
                </Alert>
              )}

              {normalizedDevices.map((d) => {
                const isSelected = draft.device_id === d.id;
                const emoji = getBrandEmoji(d.brand_name);
                return (
                  <Box
                    key={d.id}
                    onClick={() => handleSelectDevice(d)}
                    sx={{
                      border: `2px solid ${isSelected ? GREEN_ACCENT : BORDER}`,
                      bgcolor: isSelected ? "#f0fdf4" : "#fff",
                      cursor: "pointer",
                      borderRadius: 0,
                      transition: "all 150ms ease",
                      p: 0,
                      "&:hover": { borderColor: GREEN_LIGHT, bgcolor: "#f0fdf4" },
                    }}
                  >
                    <Stack direction="row" alignItems="stretch">
                      {/* Emoji box */}
                      <Box sx={{
                        width: 56,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 24,
                        bgcolor: isSelected ? GREEN_PALE : "#f3f4f6",
                        borderRight: `1px solid ${isSelected ? GREEN_ACCENT : BORDER}`,
                        flexShrink: 0,
                      }}>
                        {emoji}
                      </Box>

                      {/* Content */}
                      <Box sx={{ flex: 1, p: 1.5 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                          <Box flex={1} mr={1}>
                            <Typography sx={{ fontWeight: 800, fontSize: 14, color: INK, lineHeight: 1.2 }}>
                              {d.displayPlan || "Device"}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: INK_3, mt: 0.3 }}>
                              {d.displayName}
                            </Typography>
                            <Stack spacing={0.2} mt={0.5}>
                              {d.imei1          && <Typography sx={{ fontSize: 10, color: INK_4 }}>IMEI 1: {maskImei(d.imei1)}</Typography>}
                              {d.imei2          && <Typography sx={{ fontSize: 10, color: INK_4 }}>IMEI 2: {maskImei(d.imei2)}</Typography>}
                              {d.serial         && <Typography sx={{ fontSize: 10, color: INK_4 }}>Serial: {d.serial}</Typography>}
                              {d.available_claim != null && (
                                <Typography sx={{ fontSize: 10, color: GREEN, fontWeight: 600 }}>
                                  Claims left: {d.available_claim}
                                </Typography>
                              )}
                            </Stack>
                          </Box>
                          {isSelected
                            ? <CheckCircleRoundedIcon sx={{ color: GREEN_ACCENT, fontSize: 22, flexShrink: 0, mt: 0.2 }} />
                            : <RadioButtonUncheckedIcon sx={{ color: BORDER, fontSize: 22, flexShrink: 0, mt: 0.2 }} />
                          }
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>
                );
              })}

              {errors.device && (
                <Typography sx={{ color: "#ef4444", fontSize: 12 }}>{errors.device}</Typography>
              )}

              <TrustBadges />
            </Stack>
          )}

          {/* ──────────────────────────────────────────────────────────────────
              STEP 1 — ISSUE DETAILS
          ────────────────────────────────────────────────────────────────── */}
          {step === 1 && (
            <Stack spacing={1.5}>
              <StepHeading icon="🔧" title="Issue Details" subtitle="Select all issues affecting your device" />

              {/* Selected device chip */}
              {selectedDevice && (
                <Box sx={{
                  border: `1px solid ${GREEN_PALE}`,
                  bgcolor: "#f0fdf4",
                  px: 1.5, py: 1,
                  borderRadius: 0,
                  borderLeft: `3px solid ${GREEN_ACCENT}`,
                }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography sx={{ fontSize: 18 }}>{getBrandEmoji(selectedDevice.brand_name)}</Typography>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: 13, color: INK }}>
                        {selectedDevice.displayPlan}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: INK_3 }}>{selectedDevice.displayName}</Typography>
                    </Box>
                  </Stack>
                </Box>
              )}

              {/* Loading state */}
              {issuesLoading && (
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16} sx={{ color: GREEN }} />
                    <Typography sx={{ fontSize: 12, color: INK_3 }}>Loading available issues…</Typography>
                  </Stack>
                  <LinearProgress sx={{
                    borderRadius: 0, height: 3,
                    "& .MuiLinearProgress-bar": { bgcolor: GREEN_ACCENT, borderRadius: 0 },
                  }} />
                </Stack>
              )}

              {/* Error state */}
              {!issuesLoading && issuesError && (
                <Alert severity="error" sx={{ borderRadius: 0, fontSize: 13 }}
                  action={
                    <Button size="small" sx={{ borderRadius: 0, color: "#ef4444", fontWeight: 700 }}
                      onClick={() => fetchIssuesByProduct(selectedDevice?.productId)}>
                      Retry
                    </Button>
                  }
                >
                  {issuesError}
                </Alert>
              )}

              {/* Empty state */}
              {!issuesLoading && !issuesError && !issues.length && (
                <Typography sx={{ fontSize: 13, color: INK_3 }}>No issues available for this product.</Typography>
              )}

              {/* Issue chips (API data) */}
              {!issuesLoading && issues.length > 0 && (
                <Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: INK_2, mb: 1 }}>
                    Select Issue(s) *
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1} useFlexGap>
                    {issues.map((issue) => {
                      const selected = draft.issues.some((i) => i.id === issue.id);
                      return (
                        <Box
                          key={issue.id}
                          onClick={() => toggleIssue(issue)}
                          sx={{
                            px: 1.5, py: 0.7,
                            border: `2px solid ${selected ? GREEN_ACCENT : BORDER}`,
                            bgcolor: selected ? GREEN_PALE : "#fff",
                            cursor: "pointer",
                            borderRadius: 0,
                            display: "flex", alignItems: "center", gap: 0.7,
                            transition: "all 150ms ease",
                            "&:hover": { borderColor: GREEN_LIGHT, bgcolor: "#f0fdf4" },
                          }}
                        >
                          {selected
                            ? <CheckCircleRoundedIcon sx={{ fontSize: 15, color: GREEN_ACCENT }} />
                            : <RadioButtonUncheckedIcon sx={{ fontSize: 15, color: BORDER }} />
                          }
                          <Typography sx={{ fontSize: 13, color: selected ? GREEN : INK_2, fontWeight: selected ? 600 : 400 }}>
                            {issue.title}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              )}

              {errors.issues && (
                <Typography sx={{ color: "#ef4444", fontSize: 12 }}>{errors.issues}</Typography>
              )}

              {/* Notes */}
              <TextField
                label="Additional Notes (optional)"
                multiline
                minRows={3}
                value={draft.notes}
                onChange={(e) => setDraft((p) => ({ ...p, notes: e.target.value }))}
                placeholder="Describe the issue in detail…"
                inputProps={{ maxLength: 500 }}
                helperText={`${draft.notes.length}/500`}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 0,
                    fontSize: 13,
                    "&.Mui-focused fieldset": { borderColor: GREEN },
                  },
                  "& .MuiInputLabel-root.Mui-focused": { color: GREEN },
                }}
              />
            </Stack>
          )}

          {/* ──────────────────────────────────────────────────────────────────
              STEP 2 — UPLOAD PHOTOS (unchanged from v3, green accents)
          ────────────────────────────────────────────────────────────────── */}
          {step === 2 && (
            <Stack spacing={2}>
              <StepHeading icon="📷" title="Upload Photos" subtitle={`${draft.photos.length} / ${MAX_PHOTOS} uploaded`} />

              <InfoBox>
                Upload clear photos of the damaged device — front, back, sides, and invoice.
                Minimum {MIN_PHOTOS} · maximum {MAX_PHOTOS} photos · each ≤ {MAX_FILE_MB} MB.
              </InfoBox>

              {/* Drop zone */}
              <Box
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); handleAddPhotos(e.dataTransfer.files); }}
                sx={{
                  border: `2px dashed ${BORDER}`,
                  borderRadius: 0,
                  p: 3.5,
                  textAlign: "center",
                  bgcolor: "#fff",
                  "&:hover": { borderColor: GREEN_LIGHT, bgcolor: "#f0fdf4" },
                  transition: "all 150ms",
                }}
              >
                <Box sx={{
                  width: 44, height: 44, borderRadius: 0,
                  border: `2px solid ${GREEN}`,
                  mx: "auto", mb: 1.2,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: GREEN,
                }}>
                  <CameraAltRoundedIcon />
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: 13, color: INK, mb: 0.3 }}>
                  Drag & drop photos here
                </Typography>
                <Typography sx={{ fontSize: 11, color: INK_4, mb: 1.5 }}>
                  JPG, PNG, WEBP, HEIC supported
                </Typography>
                {/* <Button
                  component="label"
                  disabled={draft.photos.length >= MAX_PHOTOS}
                  sx={{
                    px: 4, py: 0.8,
                    borderRadius: 0,
                    background: `linear-gradient(90deg, ${GREEN} 0%, ${GREEN_ACCENT} 100%)`,
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 13,
                    textTransform: "none",
                    "&:hover": { background: `linear-gradient(90deg, ${GREEN_LIGHT}, ${GREEN_ACCENT})` },
                    "&.Mui-disabled": { bgcolor: "#e5e7eb", background: "none" },
                  }}
                >
                  Browse Files
                  <input hidden multiple type="file" accept="image/*" capture="environment"
                    onChange={(e) => handleAddPhotos(e.target.files)} />
                </Button> */}

                <Button
                  component="label"
                  disabled={draft.photos.length >= MAX_PHOTOS}
                  sx={{
                    px: 4, py: 0.8,
                    borderRadius: 0,
                    background: `linear-gradient(90deg, ${GREEN} 0%, ${GREEN_ACCENT} 100%)`,
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 13,
                    textTransform: "none",
                    position: "relative", // 1. ADDED: Keeps the invisible input contained
                    overflow: "hidden",
                    "&:hover": { background: `linear-gradient(90deg, ${GREEN_LIGHT}, ${GREEN_ACCENT})` },
                    "&.Mui-disabled": { bgcolor: "#e5e7eb", background: "none" },
                  }}
                >
                  Browse Files
                  
                  {/* 2. ADDED: Removed 'hidden' and applied the invisible overlay hack */}
                  <input 
                    multiple 
                    type="file" 
                    accept="image/*" 
                    // capture="environment"
                    onChange={(e) => handleAddPhotos(e.target.files)} 
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      opacity: 0,
                      cursor: "pointer",
                      zIndex: 999, // Forces it to sit on top of the text
                    }}
                  />
                </Button>
              </Box>

              {(photoError || errors.photos) && (
                <Typography sx={{ color: "#ef4444", fontSize: 12 }}>{photoError || errors.photos}</Typography>
              )}

              {/* Upload list */}
              {uploadingPhotos.length > 0 && (
                <Box sx={{ bgcolor: "#fff", border: `1px solid ${BORDER}`, p: 2, borderRadius: 0 }}>
                  <Stack spacing={2}>
                    {uploadingPhotos.map((p) => (
                      <Box key={p.tempId}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography sx={{ flex: 1, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", mr: 1, color: INK_2 }}>
                            {p.name}
                          </Typography>
                          {p.status !== "uploading" && (
                            <Button size="small" onClick={() => removePhoto(p.tempId)}
                              sx={{ minWidth: 0, p: "2px 8px", fontSize: 11, borderRadius: 0, color: "#ef4444", border: "1px solid #fca5a5" }}>
                              Remove
                            </Button>
                          )}
                        </Stack>
                        <Box sx={{ mt: 0.8, height: 4, borderRadius: 0, bgcolor: "#e5e7eb", overflow: "hidden" }}>
                          <Box sx={{
                            width: `${p.progress ?? 0}%`, height: "100%",
                            transition: "width 200ms ease",
                            bgcolor: p.status === "success" ? GREEN_ACCENT : p.status === "error" ? "#ef4444" : "#facc15",
                          }} />
                        </Box>
                        <Typography sx={{ fontSize: 11, color: INK_4, mt: 0.3 }}>
                          {p.status === "uploading" && `Uploading… ${p.progress ?? 0}%`}
                          {p.status === "success"   && "✅ Uploaded successfully"}
                          {p.status === "error"     && "❌ Failed — remove and retry"}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          )}

          {/* ──────────────────────────────────────────────────────────────────
              STEP 3 — SERVICE LOCATION (unchanged logic, green styling)
          ────────────────────────────────────────────────────────────────── */}
          {step === 3 && (
            <Stack spacing={2}>
              <StepHeading icon="📍" title="Service Method" subtitle="Choose how you'd like us to collect your device" />

              {/* Pickup / Drop-off toggle */}
              <Stack direction="row" spacing={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 0, overflow: "hidden" }}>
                {["pickup", "drop"].map((opt) => (
                  <Box
                    key={opt}
                    onClick={() => setDraft((p) => ({ ...p, service: opt }))}
                    sx={{
                      flex: 1, py: 1.2, px: 1,
                      textAlign: "center",
                      cursor: "pointer",
                      bgcolor: draft.service === opt ? GREEN : "#fff",
                      borderRight: opt === "pickup" ? `1px solid ${BORDER}` : "none",
                      transition: "all 150ms ease",
                    }}
                  >
                    <Stack alignItems="center" spacing={0.3}>
                      {opt === "pickup"
                        ? <LocalShippingRoundedIcon sx={{ fontSize: 18, color: draft.service === opt ? "#fff" : INK_3 }} />
                        : <StoreRoundedIcon sx={{ fontSize: 18, color: draft.service === opt ? "#fff" : INK_3 }} />
                      }
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: draft.service === opt ? "#fff" : INK_2 }}>
                        {opt === "pickup" ? "Pickup" : "Drop-off"}
                      </Typography>
                    </Stack>
                  </Box>
                ))}
              </Stack>

              {/* PICKUP addresses */}
              {draft.service === "pickup" && (
                <>
                  {normalizedAddresses.length === 0 ? (
                    <Alert severity="warning" sx={{ borderRadius: 0, fontSize: 13 }}>
                      No saved addresses found. Please add one in your Profile tab first.
                    </Alert>
                  ) : (
                    <Stack spacing={1.2}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: INK_2 }}>
                        Select Pickup Address
                      </Typography>
                      {normalizedAddresses.map((a) => {
                        const isSelected = draft.pickup_address_id === a.id;
                        return (
                          <Box
                            key={a.id}
                            onClick={() => setDraft((p) => ({ ...p, pickup_address_id: a.id }))}
                            sx={{
                              border: `2px solid ${isSelected ? GREEN_ACCENT : BORDER}`,
                              bgcolor: isSelected ? "#f0fdf4" : "#fff",
                              cursor: "pointer",
                              borderRadius: 0,
                              overflow: "hidden",
                              "&:hover": { borderColor: GREEN_LIGHT, bgcolor: "#f0fdf4" },
                              transition: "all 150ms ease",
                            }}
                          >
                            <Stack direction="row" alignItems="stretch">
                              <Box sx={{
                                width: 44, flexShrink: 0,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                bgcolor: isSelected ? GREEN_PALE : "#f3f4f6",
                                borderRight: `1px solid ${isSelected ? GREEN_ACCENT : BORDER}`,
                              }}>
                                <HomeRoundedIcon sx={{ color: isSelected ? GREEN : INK_4, fontSize: 20 }} />
                              </Box>
                              <Box sx={{ flex: 1, p: 1.2 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                  <Box flex={1} mr={1}>
                                    {a._label && (
                                      <Typography sx={{ fontWeight: 700, fontSize: 13, color: INK, mb: 0.2 }}>
                                        {a._label}
                                      </Typography>
                                    )}
                                    <Typography sx={{ fontSize: 11, color: INK_3, lineHeight: 1.5 }}>
                                      {a._display}
                                    </Typography>
                                  </Box>
                                  {isSelected
                                    ? <CheckCircleRoundedIcon sx={{ color: GREEN_ACCENT, fontSize: 20, flexShrink: 0, mt: 0.2 }} />
                                    : <RadioButtonUncheckedIcon sx={{ color: BORDER, fontSize: 20, flexShrink: 0, mt: 0.2 }} />
                                  }
                                </Stack>
                              </Box>
                            </Stack>
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </>
              )}

              {/* DROP-OFF */}
              {draft.service === "drop" && (
                <>
                  {/* Default retailer */}
                  {defaultRetailer && (
                    <Box
                      onClick={() => { setSelectedRetailer(null); setShowManualDrop(false); }}
                      sx={{
                        border: `2px solid ${activeRetailer?.id === defaultRetailer.id ? GREEN_ACCENT : BORDER}`,
                        bgcolor: activeRetailer?.id === defaultRetailer.id ? "#f0fdf4" : "#fff",
                        cursor: "pointer", borderRadius: 0,
                        overflow: "hidden",
                        "&:hover": { borderColor: GREEN_LIGHT, bgcolor: "#f0fdf4" },
                        transition: "all 150ms ease",
                      }}
                    >
                      <Stack direction="row" alignItems="stretch">
                        <Box sx={{
                          width: 44, flexShrink: 0,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          bgcolor: activeRetailer?.id === defaultRetailer.id ? GREEN_PALE : "#f3f4f6",
                          borderRight: `1px solid ${BORDER}`,
                        }}>
                          <StoreRoundedIcon sx={{ color: activeRetailer?.id === defaultRetailer.id ? GREEN : INK_4, fontSize: 20 }} />
                        </Box>
                        <Box sx={{ flex: 1, p: 1.2 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Box flex={1} mr={1}>
                              <Stack direction="row" alignItems="center" spacing={0.8} mb={0.3}>
                                <Typography sx={{ fontWeight: 700, fontSize: 13, color: INK }}>{defaultRetailer.business_name}</Typography>
                                <Chip size="small" label="Recommended" sx={{ height: 16, fontSize: 9, fontWeight: 700, bgcolor: GREEN_PALE, color: GREEN, borderRadius: 0 }} />
                              </Stack>
                              <Typography sx={{ fontSize: 11, color: INK_3, lineHeight: 1.5 }}>
                                {[defaultRetailer.address_line1, defaultRetailer.city, defaultRetailer.state, defaultRetailer.pincode].filter(Boolean).join(", ")}
                              </Typography>
                            </Box>
                            {activeRetailer?.id === defaultRetailer.id
                              ? <CheckCircleRoundedIcon sx={{ color: GREEN_ACCENT, fontSize: 20, flexShrink: 0, mt: 0.2 }} />
                              : <RadioButtonUncheckedIcon sx={{ color: BORDER, fontSize: 20, flexShrink: 0, mt: 0.2 }} />
                            }
                          </Stack>
                        </Box>
                      </Stack>
                    </Box>
                  )}

                  <Divider sx={{ my: 0.5 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: 13, color: INK_2 }}>Find another retailer</Typography>

                  {/* Pincode search */}
                  <Stack direction="row" spacing={1}>
                    <TextField
                      label="Enter Pincode" value={retailerPincode} size="small"
                      onChange={(e) => setRetailerPincode(e.target.value.replace(/\D/g, ""))}
                      inputProps={{ maxLength: 6 }}
                      onKeyDown={(e) => e.key === "Enter" && fetchRetailersByPincode()}
                      sx={{
                        flex: 1,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 0, fontSize: 13,
                          "&.Mui-focused fieldset": { borderColor: GREEN },
                        },
                        "& .MuiInputLabel-root.Mui-focused": { color: GREEN },
                      }}
                    />
                    <Button variant="outlined"
                      onClick={fetchRetailersByPincode}
                      disabled={retailerPincode.length !== 6 || retailerLoading}
                      sx={{
                        borderRadius: 0, fontWeight: 700, fontSize: 13,
                        borderColor: GREEN, color: GREEN,
                        "&:hover": { borderColor: GREEN_LIGHT, bgcolor: GREEN_PALE },
                        "&.Mui-disabled": { borderColor: BORDER },
                      }}>
                      {retailerLoading ? <CircularProgress size={18} sx={{ color: GREEN }} /> : "Search"}
                    </Button>
                  </Stack>

                  {!retailerLoading && retailerPincode.length === 6 && !retailerResults.length && (
                    <Alert severity="info" sx={{ borderRadius: 0, fontSize: 12 }}>
                      No retailers found for PIN {retailerPincode}.
                    </Alert>
                  )}

                  {retailerResults.map((r) => {
                    const isSelected = activeRetailer?.id === r.id;
                    return (
                      <Box
                        key={r.id}
                        onClick={() => { setSelectedRetailer(r); setShowManualDrop(false); }}
                        sx={{
                          border: `2px solid ${isSelected ? GREEN_ACCENT : BORDER}`,
                          bgcolor: isSelected ? "#f0fdf4" : "#fff",
                          cursor: "pointer", borderRadius: 0,
                          overflow: "hidden",
                          "&:hover": { borderColor: GREEN_LIGHT, bgcolor: "#f0fdf4" },
                          transition: "all 150ms ease",
                        }}
                      >
                        <Stack direction="row" alignItems="stretch">
                          <Box sx={{
                            width: 44, flexShrink: 0,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            bgcolor: isSelected ? GREEN_PALE : "#f3f4f6",
                            borderRight: `1px solid ${BORDER}`,
                          }}>
                            <StoreRoundedIcon sx={{ color: isSelected ? GREEN : INK_4, fontSize: 20 }} />
                          </Box>
                          <Box sx={{ flex: 1, p: 1.2 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                              <Box flex={1} mr={1}>
                                <Typography sx={{ fontWeight: 700, fontSize: 13, color: INK, mb: 0.2 }}>{r.business_name}</Typography>
                                <Typography sx={{ fontSize: 11, color: INK_3, lineHeight: 1.5 }}>
                                  {[r.address_line1, r.city, r.state, r.pincode].filter(Boolean).join(", ")}
                                </Typography>
                              </Box>
                              {isSelected
                                ? <CheckCircleRoundedIcon sx={{ color: GREEN_ACCENT, fontSize: 20, flexShrink: 0, mt: 0.2 }} />
                                : <RadioButtonUncheckedIcon sx={{ color: BORDER, fontSize: 20, flexShrink: 0, mt: 0.2 }} />
                              }
                            </Stack>
                          </Box>
                        </Stack>
                      </Box>
                    );
                  })}

                  <Divider sx={{ my: 0.5 }} />
                  <Button variant="text" size="small"
                    onClick={() => { setShowManualDrop((v) => !v); setSelectedRetailer(null); }}
                    sx={{ borderRadius: 0, justifyContent: "flex-start", color: INK_3, fontSize: 12, textTransform: "none", fontWeight: 600 }}>
                    {showManualDrop ? "✕ Cancel manual entry" : "📍 Enter location manually"}
                  </Button>

                  {showManualDrop && (
                    <Stack spacing={1.5}>
                      <InfoBox>
                        Use this if your preferred drop-off location is not listed above.
                      </InfoBox>
                      <TextField size="small" label="Business / Location Name *"
                        value={manualRetailer.business_name}
                        onChange={(e) => setManualRetailer((p) => ({ ...p, business_name: e.target.value }))}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 0, fontSize: 13 }, "& .MuiInputLabel-root.Mui-focused": { color: GREEN } }} />
                      <TextField size="small" label="Address Line"
                        value={manualRetailer.address_line1}
                        onChange={(e) => setManualRetailer((p) => ({ ...p, address_line1: e.target.value }))}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 0, fontSize: 13 }, "& .MuiInputLabel-root.Mui-focused": { color: GREEN } }} />
                      <Stack direction="row" spacing={1}>
                        <TextField size="small" label="City *" fullWidth value={manualRetailer.city}
                          onChange={(e) => setManualRetailer((p) => ({ ...p, city: e.target.value }))}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 0, fontSize: 13 }, "& .MuiInputLabel-root.Mui-focused": { color: GREEN } }} />
                        <TextField size="small" label="State" value={manualRetailer.state}
                          onChange={(e) => setManualRetailer((p) => ({ ...p, state: e.target.value }))}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 0, fontSize: 13 }, "& .MuiInputLabel-root.Mui-focused": { color: GREEN } }} />
                        <TextField size="small" label="PIN *" sx={{ width: 90, "& .MuiOutlinedInput-root": { borderRadius: 0, fontSize: 13 }, "& .MuiInputLabel-root.Mui-focused": { color: GREEN } }}
                          value={manualRetailer.pincode} inputProps={{ maxLength: 6 }}
                          onChange={(e) => setManualRetailer((p) => ({ ...p, pincode: e.target.value.replace(/\D/g, "") }))} />
                      </Stack>
                      <Button variant="outlined" size="small"
                        disabled={!manualRetailer.business_name?.trim() || !manualRetailer.city?.trim() || !manualRetailer.pincode?.trim()}
                        onClick={confirmManualRetailer}
                        sx={{
                          borderRadius: 0, fontWeight: 700, fontSize: 13,
                          borderColor: GREEN, color: GREEN,
                          "&:hover": { borderColor: GREEN_LIGHT, bgcolor: GREEN_PALE },
                          "&.Mui-disabled": { borderColor: BORDER },
                        }}>
                        ✓ Use This Location
                      </Button>
                      {selectedRetailer?._isManual && (
                        <Alert severity="success" sx={{ borderRadius: 0, fontSize: 12 }}>
                          Location saved: {selectedRetailer.business_name}
                        </Alert>
                      )}
                    </Stack>
                  )}
                </>
              )}

              {errors.location && (
                <Typography sx={{ color: "#ef4444", fontSize: 12 }}>{errors.location}</Typography>
              )}
            </Stack>
          )}

          {/* ──────────────────────────────────────────────────────────────────
              STEP 4 — REVIEW & SUBMIT
          ────────────────────────────────────────────────────────────────── */}
          {step === 4 && (
            <Stack spacing={0}>
              <StepHeading icon="✅" title="Review Your Claim" subtitle="Please verify the details before submitting" />

              {submitError && (
                <Alert severity="error" sx={{ borderRadius: 0, mb: 1.5, fontSize: 13 }} onClose={() => setSubmitError("")}>
                  {submitError}
                </Alert>
              )}

              {/* Device */}
              <SummarySection title="📱 Device" editStep={0} setStep={setStep}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Typography sx={{ fontSize: 24 }}>{getBrandEmoji(selectedDevice?.brand_name)}</Typography>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 14, color: INK }}>{selectedDevice?.displayPlan}</Typography>
                    <Typography sx={{ fontSize: 11, color: INK_3 }}>{selectedDevice?.displayName}</Typography>
                    {selectedDevice?.imei1 && (
                      <Typography sx={{ fontSize: 10, color: INK_4, mt: 0.2 }}>IMEI: {maskImei(selectedDevice.imei1)}</Typography>
                    )}
                  </Box>
                </Stack>
              </SummarySection>

              {/* Issues */}
              <SummarySection title="🔧 Issues Reported" editStep={1} setStep={setStep}>
                <Stack direction="row" flexWrap="wrap" gap={0.7} useFlexGap>
                  {draft.issues.map((i) => (
                    <Box key={i.id} sx={{ px: 1.2, py: 0.4, bgcolor: GREEN_PALE, border: `1px solid ${GREEN_ACCENT}`, borderRadius: 0 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: GREEN }}>{i.title}</Typography>
                    </Box>
                  ))}
                </Stack>
                {draft.notes && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Typography sx={{ fontSize: 11, color: INK_4, mb: 0.3 }}>Notes</Typography>
                    <Typography sx={{ fontSize: 13, color: INK_2 }}>{draft.notes}</Typography>
                  </>
                )}
              </SummarySection>

              {/* Photos */}
              <SummarySection title={`📷 Photos (${draft.photos.length})`} editStep={2} setStep={setStep}>
                {draft.photos.length === 0
                  ? <Typography sx={{ fontSize: 12, color: INK_4 }}>No photos uploaded</Typography>
                  : (
                    <Stack direction="row" flexWrap="wrap" gap={0.8} useFlexGap>
                      {draft.photos.map((p, i) => (
                        <Box
                          key={i}
                          component="img"
                          src={p.photo_url}
                          alt={`photo-${i}`}
                          onClick={() => setPreviewPhoto(p.photo_url)}
                          sx={{ width: 64, height: 64, objectFit: "cover", borderRadius: 0, border: `2px solid ${BORDER}`, cursor: "pointer", "&:hover": { borderColor: GREEN_ACCENT } }}
                        />
                      ))}
                    </Stack>
                  )
                }
              </SummarySection>

              {/* Service */}
              <SummarySection title="📍 Service Method" editStep={3} setStep={setStep}>
                <Box sx={{ mb: 1 }}>
                  <Box sx={{
                    display: "inline-flex", alignItems: "center", gap: 0.7,
                    px: 1.5, py: 0.5,
                    bgcolor: GREEN_PALE, border: `1px solid ${GREEN_ACCENT}`, borderRadius: 0,
                  }}>
                    {draft.service === "pickup"
                      ? <><LocalShippingRoundedIcon sx={{ fontSize: 15, color: GREEN }} /><Typography sx={{ fontSize: 12, fontWeight: 700, color: GREEN }}>Pickup from Address</Typography></>
                      : <><StoreRoundedIcon sx={{ fontSize: 15, color: GREEN }} /><Typography sx={{ fontSize: 12, fontWeight: 700, color: GREEN }}>Drop-off at Retailer</Typography></>
                    }
                  </Box>
                </Box>

                {draft.service === "pickup" && pickupAddress && (
                  <Box>
                    <Typography sx={{ fontSize: 11, color: INK_4, mb: 0.2 }}>Pickup Address</Typography>
                    {pickupAddress._label && <Typography sx={{ fontWeight: 700, fontSize: 13, color: INK }}>{pickupAddress._label}</Typography>}
                    <Typography sx={{ fontSize: 12, color: INK_3 }}>{pickupAddress._display}</Typography>
                  </Box>
                )}

                {draft.service === "drop" && activeRetailer && (
                  <Box>
                    <Typography sx={{ fontSize: 11, color: INK_4, mb: 0.2 }}>Drop-off Location</Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: 13, color: INK }}>{activeRetailer.business_name}</Typography>
                    <Typography sx={{ fontSize: 12, color: INK_3 }}>
                      {[activeRetailer.address_line1, activeRetailer.city, activeRetailer.state, activeRetailer.pincode].filter(Boolean).join(", ")}
                    </Typography>
                  </Box>
                )}
              </SummarySection>

              <TrustBadges />
            </Stack>
          )}

        </Box>
      )}
      {/* ══════════════════ END SCROLLABLE BODY ══════════════════ */}

      {/* ══════════════════════════════════════════════════════════════════════
          FIXED FOOTER — green gradient CTA
      ══════════════════════════════════════════════════════════════════════ */}
      {wizState === "form" && (
        <Box sx={{
          flexShrink: 0,
          px: 2.5, py: 1.8,
          borderTop: `1px solid ${BORDER}`,
          bgcolor: "#fff",
        }}>
          <Stack direction="row" spacing={1.2}>
            {/* Back button */}
            <Button
              variant="outlined"
              disabled={step === 0 || submittingAny}
              onClick={back}
              sx={{
                flex: step === 0 ? 0 : 1,
                display: step === 0 ? "none" : "flex",
                borderRadius: 0,
                fontWeight: 700,
                fontSize: 13,
                py: 1.2,
                borderColor: BORDER,
                color: INK_2,
                textTransform: "none",
                "&:hover": { borderColor: INK_3, bgcolor: GRAY_50 },
              }}
            >
              ← Back
            </Button>

            {/* Continue / Submit */}
            {step < STEPS.length - 1 ? (
              <Button
                fullWidth
                disabled={step === 1 && issuesLoading}
                onClick={handleNext}
                endIcon={step === 1 && issuesLoading ? null : <ArrowForwardRoundedIcon />}
                sx={{
                  borderRadius: 0,
                  fontWeight: 700,
                  fontSize: 13,
                  py: 1.2,
                  textTransform: "none",
                  background: `linear-gradient(90deg, ${GREEN} 0%, ${GREEN_ACCENT} 100%)`,
                  color: "#fff",
                  "&:hover": { background: `linear-gradient(90deg, ${GREEN_LIGHT}, ${GREEN_ACCENT})` },
                  "&.Mui-disabled": { background: "#e5e7eb", color: INK_4 },
                }}
              >
                {step === 1 && issuesLoading
                  ? <CircularProgress size={20} sx={{ color: "#fff" }} />
                  : "Continue"
                }
              </Button>
            ) : (
              <Button
                fullWidth
                disabled={submittingAny}
                onClick={raiseClaim}
                startIcon={!isSubmitting && <ShieldRoundedIcon />}
                sx={{
                  borderRadius: 0,
                  fontWeight: 800,
                  fontSize: 13,
                  py: 1.3,
                  textTransform: "none",
                  background: `linear-gradient(90deg, ${DARK2} 0%, ${GREEN} 50%, ${GREEN_ACCENT} 100%)`,
                  color: "#fff",
                  letterSpacing: 0.3,
                  "&:hover": { background: `linear-gradient(90deg, ${GREEN}, ${GREEN_ACCENT})` },
                  "&.Mui-disabled": { background: "#e5e7eb", color: INK_4 },
                }}
              >
                {isSubmitting
                  ? <CircularProgress size={20} sx={{ color: "#fff" }} />
                  : "Submit Claim"
                }
              </Button>
            )}
          </Stack>
        </Box>
      )}

      {/* ══════════════════ PHOTO PREVIEW DIALOG ══════════════════ */}
      <Dialog
        open={Boolean(previewPhoto)}
        onClose={() => setPreviewPhoto(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 0, bgcolor: DARK, m: 1 } }}
      >
        <Box sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>Photo Preview</Typography>
            <Box
              onClick={() => setPreviewPhoto(null)}
              sx={{
                width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center",
                bgcolor: "rgba(255,255,255,0.1)", cursor: "pointer", borderRadius: 0,
                "&:hover": { bgcolor: "rgba(255,255,255,0.18)" },
              }}
            >
              <CloseRoundedIcon sx={{ color: "#fff", fontSize: 16 }} />
            </Box>
          </Stack>
          {previewPhoto && (
            <Box
              component="img"
              src={previewPhoto}
              alt="Preview"
              sx={{ width: "100%", maxHeight: "70vh", objectFit: "contain", borderRadius: 0 }}
            />
          )}
        </Box>
      </Dialog>

    </Dialog>
  );
}

// // src/pages/components/dialogs/ClaimWizardDialog.jsx
// // ─────────────────────────────────────────────────────────────────────────────
// //  OPTIMIZED FLOW — v3
// //  ✅ borderRadius: 0 everywhere (no rounded corners)
// //  ✅ Fixed-header + scrollable-body + fixed-footer layout (mobile-friendly)
// //  ✅ All API endpoints + payload structures UNCHANGED
// // ─────────────────────────────────────────────────────────────────────────────

// import React, {
//   useEffect, useRef, useState, useMemo, useContext, useCallback,
// } from "react";
// import {
//   Dialog, Box, Typography, Stack, Button, Divider,
//   Card, CardContent, Avatar, Stepper, Step, StepLabel,
//   TextField, Checkbox, FormControl, FormLabel, FormGroup,
//   FormControlLabel, RadioGroup, Radio, Chip, CircularProgress,
//   Alert, LinearProgress,
// } from "@mui/material";
// import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
// import axios from "axios";
// import { BASE_URL }    from "../../config/api";
// import { AuthContext } from "../../context/AuthContext";

// // ─── Constants ────────────────────────────────────────────────────────────────
// const STEPS = [
//   "Select Product",
//   "Issue Details",
//   "Upload Photos",
//   "Service Location",
//   "Review & Submit",
// ];
// const MIN_PHOTOS    = 1;
// const MAX_PHOTOS    = 6;
// const MAX_FILE_MB   = 10;
// const ALLOWED_TYPES = [
//   "image/jpeg", "image/jpg", "image/png",
//   "image/webp", "image/heic", "image/heif",
// ];

// // ─── Address normalizer (handles both API shapes) ─────────────────────────────
// const normalizeAddr = (a) => {
//   if (!a) return null;
//   const line1  = a.address_line1 || a.address1 || a.street || "";
//   const city   = a.city  || "";
//   const state  = a.state || "";
//   const pin    = a.pincode || a.pin_code || a.zip || "";
//   const label  = a.label  || a.name || a.tag || "";
//   const display = [line1, city, state, pin].filter(Boolean).join(", ");
//   return { ...a, _line1: line1, _city: city, _state: state, _pin: pin, _label: label, _display: display };
// };

// // ─────────────────────────────────────────────────────────────────────────────
// export default function ClaimWizardDialog({
//   open,
//   onClose,
//   devices          = [],
//   addresses        = [],
//   channelPartners  = [],
//   retailerInfo     = null,
//   onSubmit,
//   preselectedDeviceId = "",
//   submitting: externalSubmitting = false,
// }) {
//   const { user } = useContext(AuthContext);

//   // Normalised lists
//   const normalizedDevices = useMemo(() => devices.map((d) => ({
//     ...d,
//     productId:   d.product?.id,
//     displayName: d.product_name || "Device",
//     displayPlan: `${d.brand_name || ""} ${d.model || ""}`.trim(),
//   })), [devices]);

//   const normalizedAddresses = useMemo(() => addresses.map(normalizeAddr), [addresses]);

//   // Core state
//   const [step,         setStep]         = useState(0);
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [submitError,  setSubmitError]  = useState("");

//   const [draft, setDraft] = useState({
//     device_id: "", issues: [], notes: "", photos: [],
//     service: "pickup", pickup_address_id: "", drop_partner_id: "",
//   });

//   // Issues
//   const [issues,        setIssues]        = useState([]);
//   const [issuesLoading, setIssuesLoading] = useState(false);
//   const [issuesError,   setIssuesError]   = useState("");

//   // Photos
//   const [uploadingPhotos, setUploadingPhotos] = useState([]);
//   const [photoError,      setPhotoError]      = useState("");
//   const [previewPhoto,    setPreviewPhoto]     = useState(null);
//   const photoPreviews = useRef([]);

//   // Drop-off
//   const [defaultRetailer,  setDefaultRetailer]  = useState(null);
//   const [selectedRetailer, setSelectedRetailer] = useState(null);
//   const [retailerPincode,  setRetailerPincode]  = useState("");
//   const [retailerResults,  setRetailerResults]  = useState([]);
//   const [retailerLoading,  setRetailerLoading]  = useState(false);
//   const [showManualDrop,   setShowManualDrop]   = useState(false);
//   const [manualRetailer,   setManualRetailer]   = useState({
//     business_name: "", address_line1: "", city: "", state: "", pincode: "",
//   });

//   // Validation
//   const [errors, setErrors] = useState({});

//   // Derived
//   const activeRetailer = selectedRetailer || defaultRetailer;
//   const submittingAny  = isSubmitting || externalSubmitting;
//   const selectedDevice = normalizedDevices.find((d) => d.id === draft.device_id) || null;
//   const pickupAddress  = normalizedAddresses.find((a) => a.id === draft.pickup_address_id) || null;

//   // ── Init on open ─────────────────────────────────────────────────────────
//   // eslint-disable-next-line react-hooks/exhaustive-deps
//   useEffect(() => {
//     if (!open) return;
//     const firstAddrId = addresses?.[0]?.id || "";

//     setStep(preselectedDeviceId ? 1 : 0);
//     setIsSubmitting(false);
//     setSubmitError("");
//     setDraft({ device_id: preselectedDeviceId || "", issues: [], notes: "", photos: [], service: "pickup", pickup_address_id: firstAddrId, drop_partner_id: "" });
//     setIssues([]);
//     setIssuesError("");
//     setUploadingPhotos([]);
//     setPhotoError("");
//     setPreviewPhoto(null);
//     setRetailerPincode("");
//     setRetailerResults([]);
//     setSelectedRetailer(null);
//     setDefaultRetailer(null);
//     setShowManualDrop(false);
//     setManualRetailer({ business_name: "", address_line1: "", city: "", state: "", pincode: "" });
//     setErrors({});

//     if (preselectedDeviceId) {
//       const dev = devices.find((d) => d.id === preselectedDeviceId);
//       if (dev?.product?.id) fetchIssuesByProduct(dev.product.id);
//     }
//     if (!preselectedDeviceId && devices.length === 1) {
//       const dev = devices[0];
//       setDraft((p) => ({ ...p, device_id: dev.id }));
//       if (dev?.product?.id) { fetchIssuesByProduct(dev.product.id); setStep(1); }
//     }
//   }, [open]);

//   // ── Set default retailer ──────────────────────────────────────────────────
//   useEffect(() => {
//     if (draft.service === "drop" && retailerInfo) {
//       setDefaultRetailer(retailerInfo);
//       setSelectedRetailer(null);
//     }
//   }, [draft.service, retailerInfo]);

//   // ── Cleanup ───────────────────────────────────────────────────────────────
//   useEffect(() => {
//     return () => { try { photoPreviews.current.forEach((u) => URL.revokeObjectURL(u)); } catch {} };
//   }, []);

//   // ── Navigation ────────────────────────────────────────────────────────────
//   const next = useCallback(() => setStep((s) => Math.min(STEPS.length - 1, s + 1)), []);
//   const back = useCallback(() => { setErrors({}); setStep((s) => Math.max(0, s - 1)); }, []);

//   // ── Validation ────────────────────────────────────────────────────────────
//   const validateStep = (s) => {
//     const errs = {};
//     if (s === 0 && !draft.device_id)
//       errs.device = "Please select a device to continue.";
//     if (s === 1) {
//       if (issuesLoading)              errs.issues = "Issues are still loading — please wait.";
//       else if (!draft.issues.length)  errs.issues = "Please select at least one issue.";
//     }
//     if (s === 2) {
//       const busy = uploadingPhotos.some((p) => p.status === "uploading");
//       if (draft.photos.length < MIN_PHOTOS) errs.photos = `Please upload at least ${MIN_PHOTOS} photo.`;
//       else if (busy)                         errs.photos = "Please wait for uploads to finish.";
//     }
//     if (s === 3) {
//       if (draft.service === "pickup" && !draft.pickup_address_id)
//         errs.location = "Please select a pickup address.";
//       if (draft.service === "drop") {
//         if (activeRetailer?.id) { /* ok */ }
//         else if (showManualDrop) errs.location = "Please confirm your manual location.";
//         else                     errs.location = "Please select or enter a drop-off location.";
//       }
//     }
//     setErrors(errs);
//     return !Object.keys(errs).length;
//   };

//   const handleNext = () => { if (validateStep(step)) next(); };

//   // ── Fetch coverages ───────────────────────────────────────────────────────
//   const fetchIssuesByProduct = async (productId) => {
//     if (!productId) return;
//     try {
//       setIssues([]); setIssuesLoading(true); setIssuesError("");
//       const res  = await axios.get(`${BASE_URL}warranty/coverages/3`, { params: { product_id: productId } });
//       const list = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
//       setIssues(list);
//       if (!list.length) setIssuesError("No issues available for this product.");
//     } catch {
//       setIssuesError("Failed to load issues. Tap Retry.");
//     } finally {
//       setIssuesLoading(false);
//     }
//   };

//   // ── Device select ─────────────────────────────────────────────────────────
//   const handleSelectDevice = (device) => {
//     if (!device.productId) { console.error("Product ID missing:", device); return; }
//     setDraft((p) => ({ ...p, device_id: device.id, issues: [] }));
//     setErrors({});
//     fetchIssuesByProduct(device.productId);
//     setTimeout(next, 250);
//   };

//   // ── Toggle issue ──────────────────────────────────────────────────────────
//   const toggleIssue = (issue) =>
//     setDraft((prev) => ({
//       ...prev,
//       issues: prev.issues.some((i) => i.id === issue.id)
//         ? prev.issues.filter((i) => i.id !== issue.id)
//         : [...prev.issues, { id: issue.id, title: issue.title }],
//     }));

//   // ── Image compression (unchanged) ─────────────────────────────────────────
//   const compressImage = (file, { maxWidth = 1280, maxHeight = 1280, quality = 0.7 } = {}) =>
//     new Promise((resolve, reject) => {
//       const img = new Image();
//       const url = URL.createObjectURL(file);
//       img.onload = () => {
//         let { width, height } = img;
//         if (width > maxWidth || height > maxHeight) {
//           const s = Math.min(maxWidth / width, maxHeight / height);
//           width = Math.round(width * s); height = Math.round(height * s);
//         }
//         const c = document.createElement("canvas");
//         c.width = width; c.height = height;
//         c.getContext("2d").drawImage(img, 0, 0, width, height);
//         c.toBlob((blob) => {
//           URL.revokeObjectURL(url);
//           if (!blob) { reject(new Error("Compression failed")); return; }
//           resolve(new File([blob], file.name, { type: "image/jpeg", lastModified: Date.now() }));
//         }, "image/jpeg", quality);
//       };
//       img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Load failed")); };
//       img.src = url;
//     });

//   // ── Photo upload ──────────────────────────────────────────────────────────
//   const uploadPhoto = async (file) => {
//     if (!ALLOWED_TYPES.includes(file.type?.toLowerCase())) {
//       setPhotoError(`"${file.name}" is not a supported format.`); return;
//     }
//     if (file.size > MAX_FILE_MB * 1024 * 1024) {
//       setPhotoError(`"${file.name}" exceeds ${MAX_FILE_MB} MB.`); return;
//     }
//     const active = uploadingPhotos.filter((p) => p.status !== "error").length;
//     if (draft.photos.length + active >= MAX_PHOTOS) {
//       setPhotoError(`Maximum ${MAX_PHOTOS} photos allowed.`); return;
//     }
//     setPhotoError("");
//     const tempId = `${file.name}-${Date.now()}`;
//     setUploadingPhotos((prev) => [...prev, { tempId, name: file.name, status: "uploading", progress: 0, photo_id: null, url: null }]);

//     try {
//       const compressed = await compressImage(file);
//       const fd = new FormData();
//       fd.append("w_customer_id", user?.id);
//       fd.append("photo_type", "front");
//       fd.append("photo", compressed);
//       const res = await axios.post(`${BASE_URL}warranty/claim/upload-photo`, fd, {
//         headers: { "Content-Type": "multipart/form-data" },
//         onUploadProgress: ({ loaded, total }) => {
//           const pct = Math.round((loaded / total) * 100);
//           setUploadingPhotos((prev) => prev.map((p) => p.tempId === tempId ? { ...p, progress: pct } : p));
//         },
//       });
//       if (!res.data?.status) throw new Error("Upload failed");
//       setUploadingPhotos((prev) => prev.map((p) => p.tempId === tempId
//         ? { ...p, status: "success", progress: 100, photo_id: res.data.photo_id, url: res.data.photo_url }
//         : p
//       ));
//       setDraft((prev) => ({ ...prev, photos: [...prev.photos, { photo_id: res.data.photo_id, photo_url: res.data.photo_url }] }));
//     } catch {
//       setUploadingPhotos((prev) => prev.map((p) => p.tempId === tempId ? { ...p, status: "error", progress: 0 } : p));
//     }
//   };

//   const handleAddPhotos = (files) => Array.from(files || []).forEach(uploadPhoto);

//   const removePhoto = (tempId) => {
//     const photo = uploadingPhotos.find((p) => p.tempId === tempId);
//     setUploadingPhotos((prev) => prev.filter((p) => p.tempId !== tempId));
//     if (photo?.photo_id) setDraft((prev) => ({ ...prev, photos: prev.photos.filter((p) => p.photo_id !== photo.photo_id) }));
//   };

//   // ── Retailers by pincode ──────────────────────────────────────────────────
//   const fetchRetailersByPincode = async () => {
//     if (retailerPincode.length !== 6) return;
//     try {
//       setRetailerLoading(true); setRetailerResults([]);
//       const res = await axios.post(`${BASE_URL}warranty/retailers/by-pincode`, { pincode: retailerPincode });
//       setRetailerResults(res.data?.status ? (res.data.data || []) : []);
//     } catch { setRetailerResults([]); }
//     finally { setRetailerLoading(false); }
//   };

//   // ── Manual retailer confirm ───────────────────────────────────────────────
//   const confirmManualRetailer = () => {
//     const { business_name, city, pincode } = manualRetailer;
//     if (!business_name?.trim() || !city?.trim() || !pincode?.trim()) return;
//     setSelectedRetailer({ id: `manual_${Date.now()}`, ...manualRetailer, _isManual: true });
//     setShowManualDrop(false);
//   };

//   // ── Submit ────────────────────────────────────────────────────────────────
//   const raiseClaim = async () => {
//     for (let i = 0; i < STEPS.length - 1; i++) {
//       if (!validateStep(i)) { setStep(i); return; }
//     }
//     const payload = {
//       w_customer_id:     user.id,
//       w_device_id:       draft.device_id,
//       claim_type:        draft.service,
//       issue_description: draft.issues.map((i) => i.title).join(", "),
//       coverage_ids:      draft.issues.map((i) => i.id),
//       photo_ids:         draft.photos.map((p) => p.photo_id),
//       ...(draft.service === "pickup" && { pickup_address_id: draft.pickup_address_id || 0 }),
//       ...(draft.service === "drop" && !activeRetailer?._isManual && { drop_retailer_id: activeRetailer?.id }),
//     };
//     try {
//       setIsSubmitting(true); setSubmitError("");
//       const res = await axios.post(`${BASE_URL}warranty/claim/raise`, payload);
//       if (res.data?.status) { onSubmit?.(); }
//       else throw new Error(res.data?.message || "Claim failed");
//     } catch (err) {
//       setSubmitError(err?.response?.data?.message || err.message || "Failed to raise claim.");
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   // ── Reset ─────────────────────────────────────────────────────────────────
//   const resetForm = useCallback(() => {
//     setStep(0); setIsSubmitting(false); setSubmitError("");
//     setDraft({ device_id: "", issues: [], notes: "", photos: [], service: "pickup", pickup_address_id: addresses?.[0]?.id || "", drop_partner_id: "" });
//     setIssues([]); setIssuesError(""); setErrors([]);
//     setUploadingPhotos([]); setPhotoError(""); setPreviewPhoto(null);
//     setRetailerPincode(""); setRetailerResults([]); setSelectedRetailer(null); setDefaultRetailer(null);
//     setShowManualDrop(false); setManualRetailer({ business_name: "", address_line1: "", city: "", state: "", pincode: "" });
//   }, [addresses]);

//   // ─────────────────────────────────────────────────────────────────────────
//   // RENDER — fixed header · scrollable body · fixed footer
//   // ─────────────────────────────────────────────────────────────────────────
//   return (
//     <Dialog
//       open={open}
//       onClose={!submittingAny ? onClose : null}
//       fullWidth
//       PaperProps={{
//         sx: {
//           m: "8px",
//           width: "calc(100% - 16px)",
//           maxHeight: "92dvh",          // ← caps total dialog height
//           borderRadius: 0,             // ← no rounded corners on the dialog
//           display: "flex",
//           flexDirection: "column",
//           overflow: "hidden",          // ← children handle their own scroll
//         },
//       }}
//     >

//       {/* ══════════════════ FIXED HEADER ══════════════════ */}
//       <Box sx={{ flexShrink: 0, px: 3, pt: 2.5, pb: 1.5, borderBottom: "1px solid #e5e7eb", bgcolor: "#fff" }}>
//         <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
//           <Box>
//             <Typography variant="h6" fontWeight={500}>Raise a Claim</Typography>
//             <Typography variant="body2" color="text.secondary">
//               Step {step + 1} of {STEPS.length} — {STEPS[step]}
//             </Typography>
//           </Box>
//           <Button
//             disabled={submittingAny}
//             onClick={() => { resetForm(); onClose(); }}
//             sx={{ borderRadius: 0 }}
//           >
//             Close
//           </Button>
//         </Stack>

//         <Stepper activeStep={step} alternativeLabel>
//           {STEPS.map((s) => <Step key={s}><StepLabel sx={{ "& .MuiStepIcon-root": { borderRadius: 0 } }}>{s}</StepLabel></Step>)}
//         </Stepper>
//       </Box>

//       {/* ══════════════════ SCROLLABLE BODY ══════════════════ */}
//       <Box sx={{
//         flex: 1,
//         overflowY: "auto",
//         WebkitOverflowScrolling: "touch",
//         px: 3,
//         py: 2,
//         bgcolor: "#f9fafb",
//         // hide scrollbar on webkit but keep scrollable
//         "&::-webkit-scrollbar": { width: 4 },
//         "&::-webkit-scrollbar-thumb": { bgcolor: "#d1d5db", borderRadius: 0 },
//       }}>

//         {/* ────────── STEP 0 — SELECT DEVICE ────────── */}
//         {step === 0 && (
//           <Stack spacing={1.5}>
//             <Typography fontWeight={500}>Select Device</Typography>

//             {normalizedDevices.length === 0 && (
//               <Alert severity="info" sx={{ borderRadius: 0 }}>
//                 No registered devices found. Please contact your retailer.
//               </Alert>
//             )}

//             {normalizedDevices.map((d) => (
//               <Card
//                 key={d.id}
//                 variant="outlined"
//                 onClick={() => handleSelectDevice(d)}
//                 sx={{
//                   borderRadius: 0,
//                   cursor: "pointer",
//                   borderColor: draft.device_id === d.id ? "#4f46e5" : "#e5e7eb",
//                   transition: "border-color 150ms",
//                 }}
//               >
//                 <CardContent sx={{ position: "relative" }}>
//                   {draft.device_id === d.id && (
//                     <Chip size="small" label="Selected" color="primary"
//                       sx={{ position: "absolute", top: 8, right: 8, fontWeight: 600, borderRadius: 0 }} />
//                   )}
//                   <Stack direction="row" spacing={1.5} alignItems="center">
//                     <Avatar sx={{ bgcolor: "#eef2ff", borderRadius: 0 }}>
//                       <PhoneIphoneIcon color="primary" />
//                     </Avatar>
//                     <Box flex={1}>
//                       <Typography fontWeight={500}>{d.displayPlan}</Typography>
//                       <Typography variant="caption" color="text.secondary">{d.displayName}</Typography>
//                       <Stack spacing={0.3} mt={0.5}>
//                         {d.imei1          && <Typography variant="caption" color="text.secondary"><b>IMEI 1:</b> {d.imei1}</Typography>}
//                         {d.imei2          && <Typography variant="caption" color="text.secondary"><b>IMEI 2:</b> {d.imei2}</Typography>}
//                         {d.serial         && <Typography variant="caption" color="text.secondary"><b>Serial:</b> {d.serial}</Typography>}
//                         {d.available_claim && <Typography variant="caption" color="text.secondary"><b>Claims left:</b> {d.available_claim}</Typography>}
//                       </Stack>
//                     </Box>
//                   </Stack>
//                 </CardContent>
//               </Card>
//             ))}

//             {errors.device && <Typography color="error" variant="caption">{errors.device}</Typography>}
//           </Stack>
//         )}

//         {/* ────────── STEP 1 — ISSUE DETAILS ────────── */}
//         {step === 1 && (
//           <Stack spacing={1.5}>
//             <Typography fontWeight={500}>Issue Details</Typography>

//             {selectedDevice && (
//               <Alert severity="info" icon={<PhoneIphoneIcon fontSize="small" />} sx={{ borderRadius: 0 }}>
//                 {selectedDevice.displayPlan}{selectedDevice.displayName ? ` · ${selectedDevice.displayName}` : ""}
//               </Alert>
//             )}

//             {issuesLoading && (
//               <Stack spacing={1}>
//                 <Stack direction="row" spacing={1} alignItems="center">
//                   <CircularProgress size={18} />
//                   <Typography variant="caption">Loading available issues…</Typography>
//                 </Stack>
//                 <LinearProgress sx={{ borderRadius: 0 }} />
//               </Stack>
//             )}

//             {!issuesLoading && issuesError && (
//               <Alert severity="error" sx={{ borderRadius: 0 }}
//                 action={
//                   <Button size="small" sx={{ borderRadius: 0 }}
//                     onClick={() => fetchIssuesByProduct(selectedDevice?.productId)}>
//                     Retry
//                   </Button>
//                 }
//               >
//                 {issuesError}
//               </Alert>
//             )}

//             {!issuesLoading && !issuesError && !issues.length && (
//               <Typography variant="body2" color="text.secondary">No issues available for this product.</Typography>
//             )}

//             {!issuesLoading && issues.length > 0 && (
//               <FormControl>
//                 <FormLabel>Select Issue(s) *</FormLabel>
//                 <FormGroup>
//                   {issues.map((issue) => (
//                     <FormControlLabel
//                       key={issue.id}
//                       label={issue.title}
//                       control={
//                         <Checkbox
//                           checked={draft.issues.some((i) => i.id === issue.id)}
//                           onChange={() => toggleIssue(issue)}
//                           sx={{ borderRadius: 0 }}
//                         />
//                       }
//                     />
//                   ))}
//                 </FormGroup>
//               </FormControl>
//             )}

//             {errors.issues && <Typography color="error" variant="caption">{errors.issues}</Typography>}

//             <TextField
//               label="Notes (optional)"
//               multiline minRows={3}
//               value={draft.notes}
//               onChange={(e) => setDraft((p) => ({ ...p, notes: e.target.value }))}
//               placeholder="Describe the issue in detail…"
//               inputProps={{ maxLength: 500 }}
//               helperText={`${draft.notes.length}/500`}
//               sx={{ "& .MuiOutlinedInput-root": { borderRadius: 0 } }}
//             />
//           </Stack>
//         )}

//         {/* ────────── STEP 2 — UPLOAD PHOTOS ────────── */}
//         {step === 2 && (
//           <Stack spacing={2}>
//             <Stack direction="row" justifyContent="space-between" alignItems="center">
//               <Typography fontWeight={500}>Upload Photos</Typography>
//               <Typography variant="caption" color="text.secondary">{draft.photos.length} / {MAX_PHOTOS}</Typography>
//             </Stack>

//             <Alert severity="info" sx={{ fontSize: 12, borderRadius: 0 }}>
//               Upload damage photos from front, back, sides + invoice.
//               Min {MIN_PHOTOS} · max {MAX_PHOTOS} · each ≤ {MAX_FILE_MB} MB.
//             </Alert>

//             {/* Drop zone */}
//             <Box
//               onDragOver={(e) => e.preventDefault()}
//               onDrop={(e) => { e.preventDefault(); handleAddPhotos(e.dataTransfer.files); }}
//               sx={{ border: "2px dashed #e5e7eb", borderRadius: 0, p: 4, textAlign: "center", bgcolor: "#fff" }}
//             >
//               <Box sx={{
//                 width: 40, height: 40, borderRadius: 0,
//                 border: "2px solid #3b82f6", mx: "auto", mb: 1,
//                 display: "flex", alignItems: "center", justifyContent: "center",
//                 color: "#3b82f6", fontSize: 20,
//               }}>⬆</Box>
//               <Typography fontWeight={600}>Drag your file here</Typography>
//               <Typography variant="caption" color="text.secondary">or</Typography>
//               <br />
//               <Button
//                 component="label"
//                 disabled={draft.photos.length >= MAX_PHOTOS}
//                 sx={{
//                   mt: 1, px: 4, borderRadius: 0,
//                   bgcolor: "#2196f3", color: "#fff",
//                   textTransform: "none",
//                   "&:hover": { bgcolor: "#1e88e5" },
//                   "&.Mui-disabled": { bgcolor: "#e5e7eb" },
//                 }}
//               >
//                 Browse
//                 <input hidden multiple type="file" accept="image/*" capture="environment"
//                   onChange={(e) => handleAddPhotos(e.target.files)} />
//               </Button>
//             </Box>

//             {(photoError || errors.photos) && (
//               <Typography color="error" variant="caption">{photoError || errors.photos}</Typography>
//             )}

//             {uploadingPhotos.length > 0 && (
//               <Box sx={{ bgcolor: "#fff", borderRadius: 0, border: "1px solid #e5e7eb", p: 2 }}>
//                 <Stack spacing={2}>
//                   {uploadingPhotos.map((p) => (
//                     <Box key={p.tempId}>
//                       <Stack direction="row" justifyContent="space-between" alignItems="center">
//                         <Typography fontSize={14} sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", mr: 1 }}>
//                           {p.name}
//                         </Typography>
//                         {p.status !== "uploading" && (
//                           <Button size="small" color="error" onClick={() => removePhoto(p.tempId)}
//                             sx={{ minWidth: 0, p: 0.5, fontSize: 11, borderRadius: 0 }}>
//                             Remove
//                           </Button>
//                         )}
//                       </Stack>
//                       <Box sx={{ mt: 0.8, height: 4, borderRadius: 0, bgcolor: "#e5e7eb", overflow: "hidden" }}>
//                         <Box sx={{
//                           width: `${p.progress ?? 0}%`, height: "100%",
//                           transition: "width 200ms ease",
//                           bgcolor: p.status === "success" ? "#22c55e" : p.status === "error" ? "#ef4444" : "#facc15",
//                         }} />
//                       </Box>
//                       <Typography variant="caption" sx={{ color: "#9ca3af", mt: 0.3, display: "block" }}>
//                         {p.status === "uploading" && `Uploading… ${p.progress ?? 0}%`}
//                         {p.status === "success"   && "Uploaded"}
//                         {p.status === "error"     && "Failed — remove and retry"}
//                       </Typography>
//                     </Box>
//                   ))}
//                 </Stack>
//               </Box>
//             )}
//           </Stack>
//         )}

//         {/* ────────── STEP 3 — SERVICE LOCATION ────────── */}
//         {step === 3 && (
//           <Stack spacing={2}>
//             <Typography fontWeight={500}>Service Method</Typography>

//             <RadioGroup row value={draft.service}
//               onChange={(e) => setDraft((p) => ({ ...p, service: e.target.value }))}>
//               <FormControlLabel value="pickup" control={<Radio sx={{ borderRadius: 0 }} />} label="Pickup" />
//               <FormControlLabel value="drop"   control={<Radio sx={{ borderRadius: 0 }} />} label="Drop-off" />
//             </RadioGroup>

//             {/* PICKUP */}
//             {draft.service === "pickup" && (
//               <>
//                 {normalizedAddresses.length === 0 ? (
//                   <Alert severity="warning" sx={{ borderRadius: 0 }}>
//                     No saved addresses found. Please add one in your Profile tab first.
//                   </Alert>
//                 ) : (
//                   normalizedAddresses.map((a) => (
//                     <Card key={a.id} variant="outlined"
//                       onClick={() => setDraft((p) => ({ ...p, pickup_address_id: a.id }))}
//                       sx={{ borderRadius: 0, cursor: "pointer", borderColor: draft.pickup_address_id === a.id ? "#4f46e5" : "#e5e7eb" }}
//                     >
//                       <CardContent>
//                         <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
//                           <Box>
//                             {a._label && <Typography fontWeight={500}>{a._label}</Typography>}
//                             <Typography variant="caption">{a._display}</Typography>
//                           </Box>
//                           {draft.pickup_address_id === a.id && (
//                             <Chip size="small" label="Selected" color="primary" sx={{ borderRadius: 0 }} />
//                           )}
//                         </Stack>
//                       </CardContent>
//                     </Card>
//                   ))
//                 )}
//               </>
//             )}

//             {/* DROP-OFF */}
//             {draft.service === "drop" && (
//               <>
//                 {defaultRetailer && (
//                   <Card variant="outlined"
//                     onClick={() => { setSelectedRetailer(null); setShowManualDrop(false); }}
//                     sx={{
//                       borderRadius: 0, cursor: "pointer",
//                       borderColor:     activeRetailer?.id === defaultRetailer.id ? "#4f46e5" : "#e5e7eb",
//                       backgroundColor: activeRetailer?.id === defaultRetailer.id ? "#eef2ff" : "transparent",
//                     }}
//                   >
//                     <CardContent>
//                       <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
//                         <Box>
//                           <Typography fontWeight={500}>{defaultRetailer.business_name}</Typography>
//                           <Typography variant="caption">
//                             {[defaultRetailer.address_line1, defaultRetailer.city, defaultRetailer.state, defaultRetailer.pincode].filter(Boolean).join(", ")}
//                           </Typography>
//                         </Box>
//                         {activeRetailer?.id === defaultRetailer.id && (
//                           <Chip size="small" color="primary" label="Selected" sx={{ borderRadius: 0 }} />
//                         )}
//                       </Stack>
//                     </CardContent>
//                   </Card>
//                 )}

//                 <Divider />
//                 <Typography fontWeight={400}>Find another retailer</Typography>

//                 <Stack direction="row" spacing={1}>
//                   <TextField
//                     label="Enter Pincode" value={retailerPincode} size="small"
//                     onChange={(e) => setRetailerPincode(e.target.value.replace(/\D/g, ""))}
//                     inputProps={{ maxLength: 6 }}
//                     onKeyDown={(e) => e.key === "Enter" && fetchRetailersByPincode()}
//                     sx={{ "& .MuiOutlinedInput-root": { borderRadius: 0 } }}
//                   />
//                   <Button variant="outlined" onClick={fetchRetailersByPincode}
//                     disabled={retailerPincode.length !== 6 || retailerLoading}
//                     sx={{ borderRadius: 0 }}>
//                     {retailerLoading ? <CircularProgress size={18} /> : "Search"}
//                   </Button>
//                 </Stack>

//                 {!retailerLoading && retailerPincode.length === 6 && !retailerResults.length && (
//                   <Alert severity="info" sx={{ borderRadius: 0 }}>No retailers found for PIN {retailerPincode}.</Alert>
//                 )}

//                 {retailerResults.map((r) => (
//                   <Card key={r.id} variant="outlined"
//                     onClick={() => { setSelectedRetailer(r); setShowManualDrop(false); }}
//                     sx={{
//                       borderRadius: 0, cursor: "pointer",
//                       borderColor:     activeRetailer?.id === r.id ? "#16a34a" : "#e5e7eb",
//                       backgroundColor: activeRetailer?.id === r.id ? "#ecfdf5" : "transparent",
//                     }}
//                   >
//                     <CardContent>
//                       <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
//                         <Box>
//                           <Typography fontWeight={500}>{r.business_name}</Typography>
//                           <Typography variant="caption">
//                             {[r.address_line1, r.city, r.state, r.pincode].filter(Boolean).join(", ")}
//                           </Typography>
//                         </Box>
//                         {activeRetailer?.id === r.id && (
//                           <Chip size="small" color="success" label="Selected" sx={{ borderRadius: 0 }} />
//                         )}
//                       </Stack>
//                     </CardContent>
//                   </Card>
//                 ))}

//                 <Divider />
//                 <Button variant="text" size="small" sx={{ borderRadius: 0, justifyContent: "flex-start" }}
//                   onClick={() => { setShowManualDrop((v) => !v); setSelectedRetailer(null); }}>
//                   {showManualDrop ? "Cancel manual entry" : "📍 Enter location manually"}
//                 </Button>

//                 {showManualDrop && (
//                   <Stack spacing={1.5}>
//                     <Alert severity="info" sx={{ fontSize: 12, borderRadius: 0 }}>
//                       Use this if your preferred drop-off location is not listed above.
//                     </Alert>
//                     <TextField size="small" label="Business / Location Name *"
//                       value={manualRetailer.business_name}
//                       onChange={(e) => setManualRetailer((p) => ({ ...p, business_name: e.target.value }))}
//                       sx={{ "& .MuiOutlinedInput-root": { borderRadius: 0 } }} />
//                     <TextField size="small" label="Address Line"
//                       value={manualRetailer.address_line1}
//                       onChange={(e) => setManualRetailer((p) => ({ ...p, address_line1: e.target.value }))}
//                       sx={{ "& .MuiOutlinedInput-root": { borderRadius: 0 } }} />
//                     <Stack direction="row" spacing={1}>
//                       <TextField size="small" label="City *" fullWidth value={manualRetailer.city}
//                         onChange={(e) => setManualRetailer((p) => ({ ...p, city: e.target.value }))}
//                         sx={{ "& .MuiOutlinedInput-root": { borderRadius: 0 } }} />
//                       <TextField size="small" label="State" value={manualRetailer.state}
//                         onChange={(e) => setManualRetailer((p) => ({ ...p, state: e.target.value }))}
//                         sx={{ "& .MuiOutlinedInput-root": { borderRadius: 0 } }} />
//                       <TextField size="small" label="PIN *" sx={{ width: 100, "& .MuiOutlinedInput-root": { borderRadius: 0 } }}
//                         value={manualRetailer.pincode} inputProps={{ maxLength: 6 }}
//                         onChange={(e) => setManualRetailer((p) => ({ ...p, pincode: e.target.value.replace(/\D/g, "") }))} />
//                     </Stack>
//                     <Button variant="outlined" size="small" sx={{ borderRadius: 0 }}
//                       disabled={!manualRetailer.business_name?.trim() || !manualRetailer.city?.trim() || !manualRetailer.pincode?.trim()}
//                       onClick={confirmManualRetailer}>
//                       Use This Location
//                     </Button>
//                     {selectedRetailer?._isManual && (
//                       <Alert severity="success" sx={{ borderRadius: 0 }}>Location saved: {selectedRetailer.business_name}</Alert>
//                     )}
//                   </Stack>
//                 )}
//               </>
//             )}

//             {errors.location && <Typography color="error" variant="caption">{errors.location}</Typography>}
//           </Stack>
//         )}

//         {/* ────────── STEP 4 — REVIEW & SUBMIT ────────── */}
//         {step === 4 && (
//           <Stack spacing={2}>
//             <Typography fontWeight={500}>Review Your Claim</Typography>

//             {submitError && (
//               <Alert severity="error" sx={{ borderRadius: 0 }} onClose={() => setSubmitError("")}>
//                 {submitError}
//               </Alert>
//             )}

//             {/* Device */}
//             <Card variant="outlined" sx={{ borderRadius: 0 }}>
//               <CardContent>
//                 <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
//                   <Typography fontWeight={400}>Device</Typography>
//                   <Button size="small" sx={{ borderRadius: 0 }} onClick={() => setStep(0)}>Edit</Button>
//                 </Stack>
//                 <Typography variant="body2">{selectedDevice?.displayPlan}</Typography>
//                 <Typography variant="caption" color="text.secondary" display="block">{selectedDevice?.displayName}</Typography>
//                 {selectedDevice?.imei1 && <Typography variant="caption" color="text.secondary" display="block">IMEI: {selectedDevice.imei1}</Typography>}
//               </CardContent>
//             </Card>

//             {/* Issues */}
//             <Card variant="outlined" sx={{ borderRadius: 0 }}>
//               <CardContent>
//                 <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
//                   <Typography fontWeight={400}>Issues</Typography>
//                   <Button size="small" sx={{ borderRadius: 0 }} onClick={() => setStep(1)}>Edit</Button>
//                 </Stack>
//                 <Stack direction="row" spacing={1} flexWrap="wrap">
//                   {draft.issues.map((i) => <Chip key={i.id} label={i.title} size="small" sx={{ borderRadius: 0 }} />)}
//                 </Stack>
//                 {draft.notes && (
//                   <>
//                     <Divider sx={{ my: 1 }} />
//                     <Typography variant="caption" color="text.secondary">Notes</Typography>
//                     <Typography variant="body2">{draft.notes}</Typography>
//                   </>
//                 )}
//               </CardContent>
//             </Card>

//             {/* Photos */}
//             <Card variant="outlined" sx={{ borderRadius: 0 }}>
//               <CardContent>
//                 <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
//                   <Typography fontWeight={400}>Uploaded Photos ({draft.photos.length})</Typography>
//                   <Button size="small" sx={{ borderRadius: 0 }} onClick={() => setStep(2)}>Edit</Button>
//                 </Stack>
//                 {draft.photos.length === 0
//                   ? <Typography variant="caption" color="text.secondary">No photos</Typography>
//                   : (
//                     <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
//                       {draft.photos.map((p, i) => (
//                         <Box key={i} component="img" src={p.photo_url} alt={`photo-${i}`}
//                           sx={{ width: 70, height: 70, objectFit: "cover", borderRadius: 0, border: "1px solid #e5e7eb", cursor: "pointer" }}
//                           onClick={() => setPreviewPhoto(p.photo_url)} />
//                       ))}
//                     </Stack>
//                   )
//                 }
//               </CardContent>
//             </Card>

//             {/* Service */}
//             <Card variant="outlined" sx={{ borderRadius: 0 }}>
//               <CardContent>
//                 <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
//                   <Typography fontWeight={400}>Service Method</Typography>
//                   <Button size="small" sx={{ borderRadius: 0 }} onClick={() => setStep(3)}>Edit</Button>
//                 </Stack>
//                 <Typography variant="body2">
//                   {draft.service === "pickup" ? "Pickup from Address" : "Drop-off at Retailer"}
//                 </Typography>
//                 <Divider sx={{ my: 1 }} />
//                 {draft.service === "pickup" && pickupAddress && (
//                   <>
//                     <Typography fontWeight={600} variant="body2">Pickup Address</Typography>
//                     <Typography variant="caption">{pickupAddress._display}</Typography>
//                   </>
//                 )}
//                 {draft.service === "drop" && activeRetailer && (
//                   <>
//                     <Typography fontWeight={600} variant="body2">Drop-off Location</Typography>
//                     <Typography variant="caption">
//                       {[
//                         activeRetailer.business_name,
//                         activeRetailer.address_line1,
//                         activeRetailer.city,
//                         activeRetailer.state,
//                         activeRetailer.pincode,
//                       ].filter(Boolean).join(", ")}
//                     </Typography>
//                   </>
//                 )}
//               </CardContent>
//             </Card>
//           </Stack>
//         )}

//       </Box>
//       {/* ══════════════════ END SCROLLABLE BODY ══════════════════ */}

//       {/* ══════════════════ FIXED FOOTER ══════════════════ */}
//       <Box sx={{ flexShrink: 0, px: 3, py: 2, borderTop: "1px solid #e5e7eb", bgcolor: "#fff" }}>
//         <Stack direction="row" spacing={1}>
//           <Button
//             fullWidth variant="outlined"
//             disabled={step === 0 || submittingAny}
//             onClick={back}
//             sx={{ borderRadius: 0 }}
//           >
//             Back
//           </Button>

//           {step < STEPS.length - 1 ? (
//             <Button
//               fullWidth variant="contained"
//               disabled={step === 1 && issuesLoading}
//               onClick={handleNext}
//               sx={{ borderRadius: 0 }}
//             >
//               {step === 1 && issuesLoading
//                 ? <CircularProgress size={20} color="inherit" />
//                 : "Next"
//               }
//             </Button>
//           ) : (
//             <Button
//               fullWidth variant="contained"
//               disabled={submittingAny}
//               onClick={raiseClaim}
//               sx={{ borderRadius: 0 }}
//             >
//               {isSubmitting ? <CircularProgress size={20} color="inherit" /> : "Submit Claim"}
//             </Button>
//           )}
//         </Stack>
//       </Box>

//       {/* ══════════════════ PHOTO PREVIEW ══════════════════ */}
//       <Dialog open={Boolean(previewPhoto)} onClose={() => setPreviewPhoto(null)} maxWidth="sm" fullWidth
//         PaperProps={{ sx: { borderRadius: 0 } }}>
//         <Box p={2}>
//           <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
//             <Typography fontWeight={400}>Photo Preview</Typography>
//             <Button sx={{ borderRadius: 0 }} onClick={() => setPreviewPhoto(null)}>Close</Button>
//           </Stack>
//           {previewPhoto && (
//             <Box component="img" src={previewPhoto} alt="Preview"
//               sx={{ width: "100%", maxHeight: "70vh", objectFit: "contain", borderRadius: 0, border: "1px solid #e5e7eb" }} />
//           )}
//         </Box>
//       </Dialog>

//     </Dialog>
//   );
// }

// src/pages/components/dialogs/ClaimWizardDialog.jsx
// ─────────────────────────────────────────────────────────────────────────────
// 🎯 CLAIM WIZARD — 4-step "Buy Now" process style
//
//  Step 1 → Device Selection
//  Step 2 → Issue / Coverage Selection  (fetched from API)
//  Step 3 → Pickup Address
//  Step 4 → Review & Submit
//
// API  endpoint : POST warranty/claim/raise
// Coverage API  : GET  warranty/coverages/3
// Payload       : { w_customer_id, w_device_id, claim_type, issue_description,
//                   coverage_ids, photo_ids, pickup_address_id }
//
// ⚠️  NO API or logic changes — only UI / sx styling updated.
// ─────────────────────────────────────────────────────────────────────────────

// import React, { useContext, useEffect, useState, useCallback } from "react";
// import axios from "axios";
// import {
//   Box, Typography, TextField, Chip, CircularProgress, Alert,
// } from "@mui/material";
// import CheckCircleRoundedIcon          from "@mui/icons-material/CheckCircleRounded";
// import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
// import SecurityRoundedIcon             from "@mui/icons-material/SecurityRounded";
// import FlashOnRoundedIcon              from "@mui/icons-material/FlashOnRounded";
// import VerifiedUserRoundedIcon         from "@mui/icons-material/VerifiedUserRounded";
// import SmartphoneRoundedIcon           from "@mui/icons-material/SmartphoneRounded";
// import LocationOnRoundedIcon           from "@mui/icons-material/LocationOnRounded";
// import HomeRoundedIcon                 from "@mui/icons-material/HomeRounded";
// import ReceiptLongRoundedIcon          from "@mui/icons-material/ReceiptLongRounded";

// import WizardSheet from "../wizard/WizardSheet";
// import { AuthContext } from "../../context/AuthContext";
// import { BASE_URL }    from "../../config/api";

// // ─── Design tokens ────────────────────────────────────────────────────────────
// const G      = "#15803d";
// const G_PALE = "#dcfce7";
// const INK    = "#111827";
// const INK_2  = "#374151";
// const INK_3  = "#6b7280";
// const INK_4  = "#9ca3af";
// const BORDER = "#e5e7eb";
// const GRAY50 = "#f9fafb";

// // ─── Helpers ─────────────────────────────────────────────────────────────────
// const getBrandEmoji = (name = "") => {
//   const n = (name || "").toLowerCase();
//   if (n.includes("apple") || n.includes("iphone")) return "🍎";
//   if (n.includes("samsung"))                        return "📱";
//   if (n.includes("oneplus"))                        return "🔴";
//   if (n.includes("xiaomi") || n.includes("redmi")) return "📲";
//   if (n.includes("vivo"))                           return "💙";
//   if (n.includes("oppo"))                           return "🟢";
//   if (n.includes("realme"))                         return "⚡";
//   if (n.includes("google") || n.includes("pixel")) return "🔵";
//   if (n.includes("motorola"))                       return "Ⓜ️";
//   return "📦";
// };

// const maskImei = (v = "") =>
//   v && v.length >= 6 ? v.slice(0, 4) + "···" + v.slice(-4) : (v || "—");

// const fmtDate = (d) => {
//   if (!d) return "—";
//   try {
//     return new Date(d).toLocaleDateString("en-IN", {
//       day: "2-digit", month: "short", year: "numeric",
//     });
//   } catch { return d; }
// };

// const deviceName   = (d) => d?.model_name  || d?.device_model || d?.model  || d?.name  || "Device";
// const deviceBrand  = (d) => d?.brand_name  || d?.brand        || d?.make   || "";
// const deviceImei   = (d) => d?.imei1 || d?.imei || d?.imei_no || d?.imei_number || "";
// const deviceExpiry = (d) => d?.warranty_expiry || d?.warranty_end_date || "";

// const addrLine  = (a) => [
//   a?.address_line1 || a?.address1 || a?.street || "",
//   a?.address_line2 || a?.address2 || "",
//   a?.city  || "",
//   a?.state || "",
// ].filter(Boolean).join(", ");
// const addrPin   = (a) => a?.pincode || a?.pin_code || a?.pin || a?.postal_code || "";
// const addrLabel = (a) => a?.label   || a?.tag  || a?.type  || a?.name || "";

// // ─── Main Component ───────────────────────────────────────────────────────────
// export default function ClaimWizardDialog({
//   open,
//   onClose,
//   devices         = [],
//   addresses       = [],
//   retailerInfo,
//   channelPartners = [],
//   preselectedDeviceId,
//   onSubmit,
// }) {
//   const { user } = useContext(AuthContext);
//   const customerId = user?.id;

//   // ── Step state ────────────────────────────────────────────────────────────
//   const TOTAL_STEPS = 4;
//   const STEP_NAMES  = ["Select Device", "Select Issues", "Address", "Review"];

//   const [step,       setStep]       = useState(1);
//   const [wizState,   setWizState]   = useState("form");
//   const [submitting, setSubmitting] = useState(false);
//   const [errorMsg,   setErrorMsg]   = useState("");
//   const [errors,     setErrors]     = useState({});

//   // ── Form data ─────────────────────────────────────────────────────────────
//   const [selectedDeviceId,   setSelectedDeviceId]   = useState(null);
//   const [coverages,          setCoverages]          = useState([]);
//   const [coveragesLoading,   setCoveragesLoading]   = useState(false);
//   const [selectedCoverages,  setSelectedCoverages]  = useState([]);
//   const [notes,              setNotes]              = useState("");
//   const [selectedAddressId,  setSelectedAddressId]  = useState(null);
//   const SERVICE_TYPE = "pickup";

//   // ── Derived ───────────────────────────────────────────────────────────────
//   const selectedDevice  = devices.find((d) => d.id === selectedDeviceId) || null;
//   const selectedAddress = addresses.find((a) => a.id === selectedAddressId) || null;

//   // ── Fetch coverages whenever a device is chosen ───────────────────────────
//   const fetchCoverages = useCallback(async () => {
//     try {
//       setCoveragesLoading(true);
//       setCoverages([]);
//       const res  = await axios.get(`${BASE_URL}warranty/coverages/3`);
//       const data = res.data;
//       const list = Array.isArray(data) ? data : (data?.data || []);
//       setCoverages(list);
//     } catch (err) {
//       console.error("Coverage fetch failed:", err?.response?.data || err.message);
//       setCoverages([]);
//     } finally {
//       setCoveragesLoading(false);
//     }
//   }, []);

//   // ── Reset on open ─────────────────────────────────────────────────────────
//   useEffect(() => {
//     if (!open) return;
//     setStep(1);
//     setWizState("form");
//     setSubmitting(false);
//     setErrorMsg("");
//     setErrors({});
//     setCoverages([]);
//     setSelectedCoverages([]);
//     setNotes("");
//     if (preselectedDeviceId) {
//       setSelectedDeviceId(preselectedDeviceId);
//       setStep(2);
//       fetchCoverages();
//     } else {
//       setSelectedDeviceId(null);
//     }
//     setSelectedAddressId(addresses[0]?.id ?? null);
//   }, [open, preselectedDeviceId, addresses, fetchCoverages]);

//   // ── Handle device selection ───────────────────────────────────────────────
//   const handleSelectDevice = (deviceId) => {
//     setSelectedDeviceId(deviceId);
//     setSelectedCoverages([]);
//     fetchCoverages();
//   };

//   // ── Toggle coverage ───────────────────────────────────────────────────────
//   const toggleCoverage = (cov) => {
//     setSelectedCoverages((prev) => {
//       const exists = prev.some((c) => c.id === cov.id);
//       return exists ? prev.filter((c) => c.id !== cov.id) : [...prev, cov];
//     });
//   };

//   // ── Validate per step ─────────────────────────────────────────────────────
//   const validateStep = () => {
//     const errs = {};
//     if (step === 1 && !selectedDeviceId)
//       errs.device = "Please select a device to continue.";
//     if (step === 2 && selectedCoverages.length === 0)
//       errs.issues = "Please select at least one issue.";
//     if (step === 3 && !selectedAddressId)
//       errs.address = "Please select a pickup address.";
//     setErrors(errs);
//     return Object.keys(errs).length === 0;
//   };

//   // ── Next / Back ───────────────────────────────────────────────────────────
//   const handleNext = async () => {
//     if (wizState === "fail") {
//       setWizState("form");
//       setStep(4);
//       return;
//     }
//     if (wizState === "success") {
//       onClose();
//       return;
//     }
//     if (!validateStep()) return;
//     if (step < TOTAL_STEPS) {
//       setStep((s) => s + 1);
//       return;
//     }

//     // ── SUBMIT ────────────────────────────────────────────────────────────
//     try {
//       setSubmitting(true);
//       setWizState("loading");

//       const payload = {
//         w_customer_id:     customerId,
//         w_device_id:       selectedDeviceId,
//         claim_type:        SERVICE_TYPE,
//         issue_description: selectedCoverages.map((c) => c.title).join(", "),
//         coverage_ids:      selectedCoverages.map((c) => c.id),
//         photo_ids:         [],
//         pickup_address_id: selectedAddressId || 0,
//       };

//       const res = await axios.post(`${BASE_URL}warranty/claim/raise`, payload);

//       if (res.data && res.data.status === false) {
//         throw new Error(res.data.message || "Claim submission failed");
//       }

//       setWizState("success");
//       if (onSubmit) await onSubmit();
//     } catch (err) {
//       const msg =
//         err?.response?.data?.message ||
//         err?.response?.data?.error   ||
//         err?.message                 ||
//         "Failed to submit claim. Please try again.";
//       setErrorMsg(msg);
//       setWizState("fail");
//     } finally {
//       setSubmitting(false);
//     }
//   };

//   const handleBack = () => {
//     setErrors({});
//     setStep((s) => s - 1);
//   };

//   // ── Button label ──────────────────────────────────────────────────────────
//   const primaryLabel =
//     wizState === "success" ? "Close"            :
//     wizState === "fail"    ? "Try Again"        :
//     step === TOTAL_STEPS   ? "🛡 Submit Claim"  :
//                              "Continue →";

//   // ─────────────────────────────────────────────────────────────────────────
//   return (
//     <WizardSheet
//       open={open}
//       onClose={onClose}
//       brand="Goelectro"
//       brandAccent="niX"
//       step={step}
//       totalSteps={TOTAL_STEPS}
//       stepName={STEP_NAMES[step - 1]}
//       stepNames={STEP_NAMES}
//       onBack={handleBack}
//       onNext={handleNext}
//       primaryLabel={primaryLabel}
//       primaryLoading={submitting}
//       primaryDisabled={submitting || (step === 2 && coveragesLoading)}
//       showBack={step > 1}
//       state={wizState}
//       loadingText="Submitting your claim…"
//       loadingSub="This takes just a moment"
//       successTitle="Claim Submitted! 🎉"
//       successSub="Your claim has been registered. Our team will contact you within 24 hours to arrange pickup."
//       successContent={
//         <SuccessContent
//           device={selectedDevice}
//           coverages={selectedCoverages}
//           address={selectedAddress}
//         />
//       }
//       failTitle="Submission Failed"
//       failSub={errorMsg || "Something went wrong. Please try again."}
//       failReason="Check your internet connection. If the problem persists, contact support."
//     >
//       {step === 1 && (
//         <StepDeviceSelection
//           devices={devices}
//           selectedDeviceId={selectedDeviceId}
//           onSelect={handleSelectDevice}
//           error={errors.device}
//         />
//       )}

//       {step === 2 && (
//         <StepIssueSelection
//           coverages={coverages}
//           loading={coveragesLoading}
//           selectedCoverages={selectedCoverages}
//           onToggle={toggleCoverage}
//           notes={notes}
//           onNotesChange={setNotes}
//           device={selectedDevice}
//           error={errors.issues}
//         />
//       )}

//       {step === 3 && (
//         <StepAddressSelection
//           addresses={addresses}
//           selectedAddressId={selectedAddressId}
//           onSelect={setSelectedAddressId}
//           error={errors.address}
//         />
//       )}

//       {step === 4 && (
//         <StepReview
//           device={selectedDevice}
//           coverages={selectedCoverages}
//           notes={notes}
//           address={selectedAddress}
//         />
//       )}
//     </WizardSheet>
//   );
// }

// // ─────────────────────────────────────────────────────────────────────────────
// // STEP 1 — Device Selection
// // ─────────────────────────────────────────────────────────────────────────────
// function StepDeviceSelection({ devices, selectedDeviceId, onSelect, error }) {
//   return (
//     <Box>
//       <StepHeading
//         title="Select Your Device"
//         sub="Choose the device you want to raise a claim for."
//       />
//       <TrustBadges />

//       {devices.length === 0 ? (
//         <EmptyState icon="📱" title="No Devices Registered"
//           sub="Your protected devices will appear here once registered by your retailer."
//         />
//       ) : (
//         <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
//           {devices.map((d) => {
//             const isSelected = d.id === selectedDeviceId;
//             const name       = deviceName(d);
//             const brand      = deviceBrand(d);
//             const imei       = deviceImei(d);
//             const expiry     = deviceExpiry(d);
//             const emoji      = getBrandEmoji(brand || name);

//             return (
//               <Box key={d.id} onClick={() => onSelect(d.id)}
//                 sx={{
//                   display: "flex", alignItems: "center", gap: 1.5,
//                   p: 1.75, borderRadius: 0,
//                   border: `2px solid ${isSelected ? G : BORDER}`,
//                   bgcolor: isSelected ? "#f0fdf4" : "#fff",
//                   cursor: "pointer",
//                   transition: "all 200ms ease",
//                   boxShadow: isSelected ? "0 4px 20px rgba(21,128,61,0.15)" : "none",
//                   "&:hover": { borderColor: G, bgcolor: "#f0fdf4" },
//                 }}
//               >
//                 {/* Brand emoji icon */}
//                 <Box sx={{
//                   width: 48, height: 48, borderRadius: 0, flexShrink: 0,
//                   bgcolor: isSelected ? G_PALE : GRAY50,
//                   display: "flex", alignItems: "center", justifyContent: "center",
//                   fontSize: 22, transition: "background 200ms",
//                 }}>
//                   {emoji}
//                 </Box>

//                 {/* Info */}
//                 <Box sx={{ flex: 1, minWidth: 0 }}>
//                   <Typography sx={{
//                     fontFamily: '"Sora", sans-serif', fontWeight: 700,
//                     fontSize: 14, color: isSelected ? G : INK,
//                     mb: 0.25, whiteSpace: "nowrap", overflow: "hidden",
//                     textOverflow: "ellipsis",
//                   }}>
//                     {name}
//                   </Typography>
//                   {brand && (
//                     <Typography sx={{ fontSize: 11, color: INK_4, lineHeight: 1.3 }}>
//                       {brand}
//                     </Typography>
//                   )}
//                   {imei && (
//                     <Typography sx={{ fontSize: 11, color: INK_4 }}>
//                       IMEI: {maskImei(imei)}
//                     </Typography>
//                   )}
//                   {expiry && (
//                     <Typography sx={{ fontSize: 11, color: INK_4 }}>
//                       Warranty: {fmtDate(expiry)}
//                     </Typography>
//                   )}
//                 </Box>

//                 {/* Radio indicator */}
//                 {isSelected
//                   ? <CheckCircleRoundedIcon sx={{ color: G, fontSize: 22, flexShrink: 0 }} />
//                   : <RadioButtonUncheckedRoundedIcon sx={{ color: BORDER, fontSize: 22, flexShrink: 0 }} />
//                 }
//               </Box>
//             );
//           })}
//         </Box>
//       )}

//       {error && (
//         <Alert severity="error" sx={{ mt: 1.5, fontSize: 12, borderRadius: 0 }}>
//           {error}
//         </Alert>
//       )}

//       <InfoBox color="amber">
//         💡 <strong>Tip:</strong> Can't see your device? Contact your retailer to
//         register it first.
//       </InfoBox>
//     </Box>
//   );
// }

// // ─────────────────────────────────────────────────────────────────────────────
// // STEP 2 — Coverage / Issue Selection  (live from API)
// // ─────────────────────────────────────────────────────────────────────────────
// function StepIssueSelection({
//   coverages, loading, selectedCoverages, onToggle,
//   notes, onNotesChange, device, error,
// }) {
//   return (
//     <Box>
//       <StepHeading
//         title="What Happened?"
//         sub={
//           <>
//             Select the issue(s) with your{" "}
//             <strong style={{ color: G }}>{deviceName(device)}</strong>.
//           </>
//         }
//       />

//       {loading ? (
//         <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 3 }}>
//           <CircularProgress size={20} sx={{ color: G }} />
//           <Typography sx={{ fontSize: 13, color: INK_3 }}>
//             Loading available issues…
//           </Typography>
//         </Box>
//       ) : coverages.length === 0 ? (
//         <InfoBox color="red">
//           ⚠️ Could not load issue types. Please go back and try again.
//         </InfoBox>
//       ) : (
//         <>
//           <FieldLabel text="Issue Type" required />
//           <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 0.5 }}>
//             {coverages.map((cov) => {
//               const active = selectedCoverages.some((c) => c.id === cov.id);
//               return (
//                 <Chip
//                   key={cov.id}
//                   label={cov.title || cov.name || `Issue ${cov.id}`}
//                   onClick={() => onToggle(cov)}
//                   sx={{
//                     fontFamily: '"DM Sans", sans-serif',
//                     fontWeight: 600, fontSize: 12, height: 34,
//                     borderRadius: 0,
//                     border: `1.5px solid ${active ? G : BORDER}`,
//                     bgcolor: active ? G_PALE : "#fff",
//                     color: active ? G : INK_2,
//                     cursor: "pointer",
//                     transition: "all 160ms ease",
//                     "& .MuiChip-label": { px: 1.25 },
//                     "&:hover": { borderColor: G, bgcolor: G_PALE, color: G },
//                   }}
//                 />
//               );
//             })}
//           </Box>
//           {error && (
//             <Typography sx={{ fontSize: 11, color: "#dc2626", mt: 0.75, fontWeight: 500 }}>
//               {error}
//             </Typography>
//           )}
//         </>
//       )}

//       {/* Additional notes */}
//       <Box sx={{ mt: 2.5 }}>
//         <FieldLabel text="Additional Notes (optional)" />
//         <TextField
//           fullWidth multiline rows={3}
//           placeholder="Describe what happened in detail…"
//           value={notes}
//           onChange={(e) => onNotesChange(e.target.value)}
//           inputProps={{ maxLength: 500 }}
//           sx={{
//             "& .MuiOutlinedInput-root": { fontSize: 13, borderRadius: 0 },
//           }}
//         />
//         <Typography sx={{ fontSize: 11, color: INK_4, mt: 0.5, textAlign: "right" }}>
//           {notes.length}/500
//         </Typography>
//       </Box>

//       <InfoBox color="green">
//         📋 <strong>Note:</strong> Be specific — it helps our team prepare the right spare parts.
//       </InfoBox>
//     </Box>
//   );
// }

// // ─────────────────────────────────────────────────────────────────────────────
// // STEP 3 — Address Selection
// // ─────────────────────────────────────────────────────────────────────────────
// function StepAddressSelection({ addresses, selectedAddressId, onSelect, error }) {
//   return (
//     <Box>
//       <StepHeading
//         title="Pickup Address"
//         sub="Where should our team collect your device?"
//       />

//       {addresses.length === 0 ? (
//         <EmptyState icon="🏠" title="No Addresses Saved"
//           sub="Please add a pickup address in your Profile tab first, then come back here."
//         />
//       ) : (
//         <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
//           {addresses.map((addr) => {
//             const isSelected = addr.id === selectedAddressId;
//             const label      = addrLabel(addr);
//             const line       = addrLine(addr);
//             const pin        = addrPin(addr);

//             return (
//               <Box key={addr.id} onClick={() => onSelect(addr.id)}
//                 sx={{
//                   display: "flex", alignItems: "flex-start", gap: 1.5,
//                   p: 1.75, borderRadius: 0,
//                   border: `2px solid ${isSelected ? G : BORDER}`,
//                   bgcolor: isSelected ? "#f0fdf4" : "#fff",
//                   cursor: "pointer",
//                   transition: "all 200ms ease",
//                   boxShadow: isSelected ? "0 4px 20px rgba(21,128,61,0.15)" : "none",
//                   "&:hover": { borderColor: G, bgcolor: "#f0fdf4" },
//                 }}
//               >
//                 <Box sx={{
//                   width: 40, height: 40, borderRadius: 0, flexShrink: 0,
//                   bgcolor: isSelected ? G_PALE : GRAY50,
//                   display: "flex", alignItems: "center", justifyContent: "center",
//                   transition: "background 200ms",
//                 }}>
//                   <HomeRoundedIcon sx={{ fontSize: 20, color: isSelected ? G : INK_4 }} />
//                 </Box>

//                 <Box sx={{ flex: 1, minWidth: 0 }}>
//                   {label && (
//                     <Typography sx={{
//                       fontFamily: '"Sora", sans-serif', fontWeight: 700,
//                       fontSize: 13, color: isSelected ? G : INK,
//                       mb: 0.25, textTransform: "capitalize",
//                     }}>
//                       {label}
//                     </Typography>
//                   )}
//                   <Typography sx={{ fontSize: 12, color: INK_3, lineHeight: 1.5 }}>
//                     {line || "—"}
//                   </Typography>
//                   {pin && (
//                     <Typography sx={{ fontSize: 11, color: INK_4, mt: 0.25 }}>
//                       PIN: {pin}
//                     </Typography>
//                   )}
//                 </Box>

//                 {isSelected
//                   ? <CheckCircleRoundedIcon sx={{ color: G, fontSize: 22, flexShrink: 0 }} />
//                   : <RadioButtonUncheckedRoundedIcon sx={{ color: BORDER, fontSize: 22, flexShrink: 0 }} />
//                 }
//               </Box>
//             );
//           })}
//         </Box>
//       )}

//       {error && (
//         <Alert severity="error" sx={{ mt: 1.5, fontSize: 12, borderRadius: 0 }}>
//           {error}
//         </Alert>
//       )}

//       {/* Pickup info box */}
//       <Box sx={{
//         mt: 2, p: 1.5, bgcolor: GRAY50, borderRadius: 0,
//         border: `1px solid ${BORDER}`,
//       }}>
//         <Typography sx={{ fontSize: 11, fontWeight: 700, color: INK_2, mb: 0.5 }}>
//           🚚 Doorstep Pickup
//         </Typography>
//         <Typography sx={{ fontSize: 12, color: INK_3, lineHeight: 1.5 }}>
//           Our team picks up your device within 24 hours of claim approval — no
//           trip to a service centre needed.
//         </Typography>
//       </Box>
//     </Box>
//   );
// }

// // ─────────────────────────────────────────────────────────────────────────────
// // STEP 4 — Review
// // ─────────────────────────────────────────────────────────────────────────────
// function StepReview({ device, coverages, notes, address }) {
//   return (
//     <Box>
//       <StepHeading
//         title="Review Your Claim"
//         sub="Double-check everything before submitting."
//       />

//       {/* Device */}
//       <ReviewCard icon={<SmartphoneRoundedIcon sx={{ fontSize: 16, color: G }} />} title="Device">
//         <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//           <Typography sx={{ fontSize: 22 }}>
//             {getBrandEmoji(deviceBrand(device) || deviceName(device))}
//           </Typography>
//           <Box>
//             <Typography sx={{
//               fontFamily: '"Sora", sans-serif', fontWeight: 700,
//               fontSize: 14, color: INK,
//             }}>
//               {deviceName(device)}
//             </Typography>
//             {deviceImei(device) && (
//               <Typography sx={{ fontSize: 11, color: INK_4 }}>
//                 IMEI: {maskImei(deviceImei(device))}
//               </Typography>
//             )}
//           </Box>
//         </Box>
//       </ReviewCard>

//       {/* Issues */}
//       <ReviewCard icon={<ReceiptLongRoundedIcon sx={{ fontSize: 16, color: G }} />} title="Issues Reported">
//         <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: notes ? 1 : 0 }}>
//           {coverages.map((cov) => (
//             <Chip key={cov.id}
//               label={cov.title || cov.name}
//               size="small"
//               sx={{
//                 borderRadius: 0,
//                 bgcolor: G_PALE, color: G,
//                 fontWeight: 700, fontSize: 11, height: 24,
//                 "& .MuiChip-label": { px: 1 },
//               }}
//             />
//           ))}
//         </Box>
//         {notes && (
//           <Typography sx={{
//             fontSize: 12, color: INK_3, lineHeight: 1.5,
//             bgcolor: GRAY50, p: 1, borderRadius: 0,
//             border: `1px solid ${BORDER}`,
//           }}>
//             {notes}
//           </Typography>
//         )}
//       </ReviewCard>

//       {/* Address */}
//       <ReviewCard icon={<LocationOnRoundedIcon sx={{ fontSize: 16, color: G }} />} title="Pickup Address">
//         {address ? (
//           <Box>
//             {addrLabel(address) && (
//               <Typography sx={{
//                 fontFamily: '"Sora", sans-serif', fontWeight: 700,
//                 fontSize: 13, color: INK, mb: 0.25, textTransform: "capitalize",
//               }}>
//                 {addrLabel(address)}
//               </Typography>
//             )}
//             <Typography sx={{ fontSize: 12, color: INK_3, lineHeight: 1.5 }}>
//               {addrLine(address) || "—"}
//             </Typography>
//             {addrPin(address) && (
//               <Typography sx={{ fontSize: 11, color: INK_4, mt: 0.25 }}>
//                 PIN: {addrPin(address)}
//               </Typography>
//             )}
//           </Box>
//         ) : (
//           <Typography sx={{ fontSize: 12, color: INK_4 }}>No address selected</Typography>
//         )}
//       </ReviewCard>

//       <InfoBox color="green">
//         ✅ <strong>Ready?</strong> Our team reviews claims within 24 hours and
//         will contact you to arrange pickup. Track status in the Claims tab.
//       </InfoBox>
//     </Box>
//   );
// }

// // ─────────────────────────────────────────────────────────────────────────────
// // Success screen content
// // ─────────────────────────────────────────────────────────────────────────────
// function SuccessContent({ device, coverages, address }) {
//   return (
//     <Box>
//       {/* Dark policy-style reference card */}
//       <Box sx={{
//         background: "linear-gradient(135deg, #111827, #1e293b)",
//         borderRadius: 0, p: 2, mb: 2, position: "relative", overflow: "hidden",
//       }}>
//         <Box sx={{
//           position: "absolute", right: -20, bottom: -20,
//           width: 100, height: 100, borderRadius: "50%",
//           bgcolor: "rgba(34,197,94,0.08)",
//         }} />
//         <Typography sx={{
//           fontSize: 10, fontWeight: 700, letterSpacing: 2,
//           color: "#4ade80", textTransform: "uppercase", mb: 0.5,
//         }}>
//           Claim Reference
//         </Typography>
//         <Typography sx={{
//           fontFamily: '"Sora", sans-serif', fontSize: 15,
//           fontWeight: 700, color: "#fff", mb: 1.5, letterSpacing: 1,
//         }}>
//           GX-CLM-{Math.floor(Math.random() * 9000000 + 1000000)}
//         </Typography>
//         {[
//           ["Device",  deviceName(device)],
//           ["Issues",  coverages.map((c) => c.title || c.name).join(", ") || "—"],
//           ["Status",  "Under Review"],
//           ["Pickup",  "Within 24 hours"],
//         ].map(([lbl, val]) => (
//           <Box key={lbl} sx={{
//             display: "flex", justifyContent: "space-between",
//             fontSize: 12, py: 0.5,
//             borderBottom: "1px solid rgba(255,255,255,0.06)",
//           }}>
//             <Box component="span" sx={{ color: "rgba(255,255,255,0.45)" }}>{lbl}</Box>
//             <Box component="span" sx={{
//               color: "rgba(255,255,255,0.9)", fontWeight: 600, maxWidth: "60%",
//               textAlign: "right", wordBreak: "break-word",
//             }}>
//               {val}
//             </Box>
//           </Box>
//         ))}
//       </Box>

//       {/* Notification channels */}
//       <Box sx={{ textAlign: "left" }}>
//         <Typography sx={{
//           fontSize: 11, fontWeight: 700, color: INK_4,
//           textTransform: "uppercase", letterSpacing: 0.5, mb: 1,
//         }}>
//           Updates will be sent via
//         </Typography>
//         {["SMS to your registered mobile", "WhatsApp confirmation", "App notifications"].map((msg) => (
//           <Box key={msg} sx={{
//             display: "flex", alignItems: "center", gap: 1,
//             py: 0.5, fontSize: 12, color: INK_3,
//           }}>
//             <Box sx={{ width: 7, height: 7, borderRadius: 0, bgcolor: "#22c55e", flexShrink: 0 }} />
//             {msg}
//           </Box>
//         ))}
//       </Box>
//     </Box>
//   );
// }

// // ─────────────────────────────────────────────────────────────────────────────
// // Reusable small components
// // ─────────────────────────────────────────────────────────────────────────────
// function StepHeading({ title, sub }) {
//   return (
//     <Box sx={{ mb: 2.5 }}>
//       <Typography sx={{
//         fontFamily: '"Sora", sans-serif', fontWeight: 800,
//         fontSize: 18, color: INK, mb: 0.5, lineHeight: 1.25,
//       }}>
//         {title}
//       </Typography>
//       <Typography sx={{ fontSize: 13, color: INK_3, lineHeight: 1.5 }}>
//         {sub}
//       </Typography>
//     </Box>
//   );
// }

// function FieldLabel({ text, required }) {
//   return (
//     <Typography sx={{
//       fontSize: 11, fontWeight: 700, color: INK_2,
//       letterSpacing: 0.6, textTransform: "uppercase", mb: 1,
//     }}>
//       {text}{required && <span style={{ color: "#ef4444" }}> *</span>}
//     </Typography>
//   );
// }

// function TrustBadges() {
//   return (
//     <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
//       {[
//         { icon: <SecurityRoundedIcon   sx={{ fontSize: 12, color: G }} />, label: "100% Secure" },
//         { icon: <FlashOnRoundedIcon    sx={{ fontSize: 12, color: G }} />, label: "24h Support" },
//         { icon: <VerifiedUserRoundedIcon sx={{ fontSize: 12, color: G }} />, label: "Verified" },
//       ].map(({ icon, label }) => (
//         <Box key={label} sx={{
//           display: "flex", alignItems: "center", gap: 0.5,
//           bgcolor: "#fff", border: `1px solid ${BORDER}`,
//           borderRadius: 0, px: 1.25, py: 0.5,
//           fontSize: 10, fontWeight: 700, color: INK_2,
//         }}>
//           {icon} {label}
//         </Box>
//       ))}
//     </Box>
//   );
// }

// function ReviewCard({ icon, title, children }) {
//   return (
//     <Box sx={{
//       mb: 1.5, bgcolor: GRAY50, border: `1px solid ${BORDER}`,
//       borderRadius: 0, overflow: "hidden",
//     }}>
//       {/* Header row */}
//       <Box sx={{
//         display: "flex", alignItems: "center", gap: 0.75,
//         px: 1.75, py: 1, borderBottom: `1px solid ${BORDER}`, bgcolor: "#fff",
//       }}>
//         {icon}
//         <Typography sx={{
//           fontSize: 11, fontWeight: 700, color: INK_2,
//           textTransform: "uppercase", letterSpacing: 0.5,
//         }}>
//           {title}
//         </Typography>
//       </Box>
//       <Box sx={{ px: 1.75, py: 1.25 }}>{children}</Box>
//     </Box>
//   );
// }

// function InfoBox({ color = "green", children }) {
//   const styles = {
//     green: { bg: "#f0fdf4", border: "1px solid #bbf7d0", color: "#166534" },
//     amber: { bg: "#fff7ed", border: "1px solid #fed7aa", color: "#92400e" },
//     red:   { bg: "#fff5f5", border: "1px solid #fecaca", color: "#b91c1c" },
//   }[color] || {};

//   return (
//     <Box sx={{
//       mt: 2, p: 1.5, borderRadius: 0,
//       bgcolor: styles.bg, border: styles.border,
//       fontSize: 12, color: styles.color, lineHeight: 1.5,
//     }}>
//       {children}
//     </Box>
//   );
// }

// function EmptyState({ icon, title, sub }) {
//   return (
//     <Box sx={{
//       textAlign: "center", py: 4, bgcolor: GRAY50,
//       borderRadius: 0, border: `2px dashed ${BORDER}`, mb: 1,
//     }}>
//       <Typography sx={{ fontSize: 36, mb: 1 }}>{icon}</Typography>
//       <Typography sx={{
//         fontFamily: '"Sora", sans-serif', fontWeight: 700,
//         fontSize: 14, color: INK, mb: 0.5,
//       }}>
//         {title}
//       </Typography>
//       <Typography sx={{ fontSize: 12, color: INK_4, lineHeight: 1.5, px: 2 }}>
//         {sub}
//       </Typography>
//     </Box>
//   );
// }

// import React, { useEffect, useRef, useState, useMemo, useContext } from "react";
// import {
//   Dialog,
//   Box,
//   Typography,
//   Stack,
//   Button,
//   Divider,
//   Card,
//   CardContent,
//   Avatar,
//   Stepper,
//   Step,
//   StepLabel,
//   TextField,
//   Checkbox,
//   FormControl,
//   FormLabel,
//   FormGroup,
//   FormControlLabel,
//   RadioGroup,
//   Radio,
//   Chip,
//   CircularProgress,
// } from "@mui/material";

// import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
// import StoreIcon from "@mui/icons-material/Store";
// import OpenInNewIcon from "@mui/icons-material/OpenInNew";
// import axios from "axios";
// import { BASE_URL } from "../../config/api";
// import { AuthContext } from "../../context/AuthContext";

// /**
//  * ClaimWizardDialog
//  */
// export default function ClaimWizardDialog({
//   open,
//   onClose,
//   // resetForm,
//   devices = [],
//   addresses = [],
//   channelPartners = [],
//   retailerInfo = null,
//   onSubmit,
//   preselectedDeviceId = "",
//   submitting = false,
// }) {
//   const STEPS = [
//     "Select Product",
//     "Issue Details",
//     "Upload Photos",
//     "Service Location",
//     "Review & Submit",
//   ];

//    const { user } = useContext(AuthContext);
//   /* ---------------- NORMALIZE DEVICES ---------------- */
//   const normalizedDevices = useMemo(() => {
//     return devices.map((d) => ({
//       ...d,
//       productId: d.product?.id,          // ✅ THIS IS THE KEY FIX
//       displayName: d.product_name || "Device",
//       displayPlan: `${d.brand_name || ""} ${d.model || ""}`.trim(),
//       displayimei: d.imei1,
//     }));
//   }, [devices]);


//   /* ---------------- STATE ---------------- */
//   const [step, setStep] = useState(0);
//   const [uploadingPhotos, setUploadingPhotos] = useState([]);
//   const [draft, setDraft] = useState({
//     device_id: "",
//     issues: [],
//     notes: "",
//     photos: [],
//     service: "pickup",
//     pickup_address_id: "",
//     drop_partner_id: "",
//   });

//   const [issues, setIssues] = useState([]);

//   const [issuesLoading, setIssuesLoading] = useState(false);
//   const [issuesError, setIssuesError] = useState("");

//   const photoPreviews = useRef([]);
//   const [previewPhoto, setPreviewPhoto] = useState(null);

//   const [defaultRetailer, setDefaultRetailer] = useState(null);
//   const [selectedRetailer, setSelectedRetailer] = useState(null);

//   const [retailerPincode, setRetailerPincode] = useState("");
//   const [retailerResults, setRetailerResults] = useState([]);
//   const [retailerLoading, setRetailerLoading] = useState(false);

//   const [errors, setErrors] = useState({});


//   const activeRetailer = selectedRetailer || defaultRetailer;

//   useEffect(() => {
//     if (draft.service === "drop" && retailerInfo) {
//       setDefaultRetailer(retailerInfo);
//       setSelectedRetailer(null); // 👈 default is selected initially
//     }
//   }, [draft.service, retailerInfo]);

//   const validateStep = (currentStep) => {
//   const newErrors = {};

//   // STEP 0 – Device
//   if (currentStep === 0) {
//     if (!draft.device_id) {
//       newErrors.device = "Please select a device";
//     }
//   }

//   // STEP 1 – Issues
//   if (currentStep === 1) {
//     if (draft.issues.length === 0) {
//       newErrors.issues = "Please select at least one issue";
//     }
//   }

//   // STEP 2 – Photos
//   if (currentStep === 2) {
//     if (draft.photos.length === 0) {
//       newErrors.photos = "Please upload at least one photo";
//     }

//     const uploading = uploadingPhotos.some(
//       (p) => p.status === "uploading"
//     );

//     if (uploading) {
//       newErrors.photos = "Please wait for photo upload to finish";
//     }
//   }

//   // STEP 3 – Service Location
//   if (currentStep === 3) {
//     if (draft.service === "pickup") {
//       if (!draft.pickup_address_id) {
//         newErrors.location = "Please select a pickup address";
//       }
//     }

//     if (draft.service === "drop") {
//       if (!activeRetailer?.id) {
//         newErrors.location = "Please select a drop-off retailer";
//       }
//     }
//   }

//   setErrors(newErrors);
//   return Object.keys(newErrors).length === 0;
// };

//   /* ---------------- INIT ---------------- */
//   useEffect(() => {
//     if (open) {
//       setDraft({
//         device_id: preselectedDeviceId || "",
//         issues: [],
//         notes: "",
//         photos: [],
//         service: "pickup",
//         pickup_address_id: addresses?.[0]?.id || "",
//         drop_partner_id: "",
//       });
//       setIssues([]);
//       setStep(preselectedDeviceId ? 1 : 0);
//     }
//   }, [open, preselectedDeviceId, addresses]);

//   useEffect(() => {
//     return () => {
//       try {
//         photoPreviews.current.forEach((u) => URL.revokeObjectURL(u));
//       } catch {}
//     };
//   }, []);

//   /* ---------------- HELPERS ---------------- */
//   const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
//   const back = () => setStep((s) => Math.max(0, s - 1));

//   const handleNext = () => {
//     if (!validateStep(step)) return;
//     next();
//   };

//   const toggleIssue = (issue) => {
//   setDraft((prev) => {
//     const exists = prev.issues.find((i) => i.id === issue.id);

//     return {
//       ...prev,
//       issues: exists
//         ? prev.issues.filter((i) => i.id !== issue.id)
//         : [...prev.issues, { id: issue.id, title: issue.title }],
//     };
//   });
// };


//   /* ---------------- FETCH ISSUES ---------------- */
//   const fetchIssuesByProduct = async (productId) => {
//   try {
//     setIssues([]);
//     setIssuesLoading(true);
//     setIssuesError("");

//     const res = await axios.get(
//       `${BASE_URL}warranty/coverages/3`,
//       {
//         params: { product_id: productId },
//       }
//     );

//     if (res.data?.status) {
//       setIssues(res.data.data || []);
//     } else {
//       setIssues([]);
//     }
//   } catch (err) {
//     setIssuesError("Failed to load issues");
//     setIssues([]);
//   } finally {
//     setIssuesLoading(false);
//   }
// };

//   /* ---------------- HANDLE DEVICE SELECT ---------------- */
//   const handleSelectDevice = (device) => {
//       if (!device.productId) {
//         console.error("Product ID missing for device:", device);
//         return;
//       }

//       setDraft((p) => ({
//         ...p,
//         device_id: device.id,
//         issues: [],
//       }));

//       // ✅ EXACTLY FROM devices.product.id
//       fetchIssuesByProduct(device.productId);
//     };

//  const uploadPhoto = async (file) => {
//   const tempId = `${file.name}-${Date.now()}`;

//   setUploadingPhotos((prev) => [
//     ...prev,
//     {
//       tempId,
//       name: file.name,
//       status: "uploading",
//       photo_id: null,
//       url: null,
//     },
//   ]);

//   try {
//     // 🔥 COMPRESS IMAGE BEFORE UPLOAD
//     const compressedFile = await compressImage(file, {
//       maxWidth: 1280,
//       maxHeight: 1280,
//       quality: 0.7, // 🔥 tweak: 0.6–0.8
//     });

//     const formData = new FormData();
//     formData.append("w_customer_id", user?.id);
//     formData.append("photo_type", "front");
//     formData.append("photo", compressedFile);

//     const res = await axios.post(
//       `${BASE_URL}warranty/claim/upload-photo`,
//       formData,
//       { headers: { "Content-Type": "multipart/form-data" } }
//     );

//     if (!res.data?.status) throw new Error("Upload failed");

//     // ✅ Update UI list
//     setUploadingPhotos((prev) =>
//       prev.map((p) =>
//         p.tempId === tempId
//           ? {
//               ...p,
//               status: "success",
//               photo_id: res.data.photo_id,
//               url: res.data.photo_url,
//             }
//           : p
//       )
//     );

//     // ✅ Store for claim submit
//     setDraft((prev) => ({
//       ...prev,
//       photos: [
//         ...prev.photos,
//         {
//           photo_id: res.data.photo_id,
//           photo_url: res.data.photo_url,
//         },
//       ],
//     }));

//      console.log(
//     "Original:",
//     (file.size / 1024 / 1024).toFixed(2), "MB",
//     "Compressed:",
//     (compressedFile.size / 1024 / 1024).toFixed(2), "MB"
//   );
//   } catch (err) {
//     console.error(err);
//     setUploadingPhotos((prev) =>
//       prev.map((p) =>
//         p.tempId === tempId
//           ? { ...p, status: "error" }
//           : p
//       )
//     );
//   }
// };



//   const handleAddPhotos = (files) => {
//     const fileArr = Array.from(files || []);
//     fileArr.forEach(uploadPhoto);
//   };

//   const fetchRetailersByPincode = async () => {
//   if (!retailerPincode || retailerPincode.length !== 6) return;

//   try {
//     setRetailerLoading(true);

//     const res = await axios.post(
//       `${BASE_URL}warranty/retailers/by-pincode`,
//       { pincode: retailerPincode }
//     );

//     if (res.data?.status) {
//       setRetailerResults(res.data.data || []);
//     } else {
//       setRetailerResults([]);
//     }
//   } catch (err) {
//     setRetailerResults([]);
//   } finally {
//     setRetailerLoading(false);
//   }
// };

//   useEffect(() => {
//     if (draft.service === "drop" && retailerInfo) {
//       setDefaultRetailer(retailerInfo);
//       setSelectedRetailer(null); // reset optional selection
//     }
//   }, [draft.service, retailerInfo]);

//   const selectedDevice = normalizedDevices.find(
//     (d) => d.id === draft.device_id
//   );

//   const pickupAddress = addresses.find(
//     (a) => a.id === draft.pickup_address_id
//   );

//   const payload = {
//     w_customer_id: user.id,
//     w_device_id: draft.device_id,
//     claim_type: draft.service, // pickup | drop

//     issue_description: draft.issues.map(i => i.title).join(", "),
//     coverage_ids: draft.issues.map(i => i.id),
//     photo_ids: draft.photos.map(p => p.photo_id),

//     ...(draft.service === "pickup" && {
//       pickup_address_id: draft.pickup_address_id || 0,
//     }),

//     ...(draft.service === "drop" && {
//       drop_retailer_id: activeRetailer?.id,
//     }),
//   };


//   const raiseClaim = async () => {
//     // 🔒 final full validation
//     for (let i = 0; i <= 3; i++) {
//       if (!validateStep(i)) {
//         setStep(i);
//         return;
//       }
//     }

//     try {
//       const res = await axios.post(
//         `${BASE_URL}warranty/claim/raise`,
//         payload
//       );

//       if (res.data?.status) {
//         onSubmit?.();
//       } else {
//         throw new Error(res.data?.message || "Claim failed");
//       }
//     } catch (err) {
//       alert(err?.response?.data?.message || "Failed to raise claim");
//     }
//   };

//   const compressImage = (
//   file,
//   {
//     maxWidth = 1280,
//     maxHeight = 1280,
//     quality = 0.7,
//   } = {}
// ) => {
//   return new Promise((resolve, reject) => {
//     const img = new Image();
//     const url = URL.createObjectURL(file);

//     img.onload = () => {
//       let { width, height } = img;

//       // 🔹 Maintain aspect ratio
//       if (width > maxWidth || height > maxHeight) {
//         const scale = Math.min(
//           maxWidth / width,
//           maxHeight / height
//         );
//         width = Math.round(width * scale);
//         height = Math.round(height * scale);
//       }

//       const canvas = document.createElement("canvas");
//       canvas.width = width;
//       canvas.height = height;

//       const ctx = canvas.getContext("2d");
//       ctx.drawImage(img, 0, 0, width, height);

//       canvas.toBlob(
//         (blob) => {
//           URL.revokeObjectURL(url);

//           if (!blob) {
//             reject(new Error("Image compression failed"));
//             return;
//           }

//           const compressedFile = new File([blob], file.name, {
//             type: "image/jpeg",
//             lastModified: Date.now(),
//           });

//           resolve(compressedFile);
//         },
//         "image/jpeg",
//         quality
//       );
//     };

//     img.onerror = () => {
//       URL.revokeObjectURL(url);
//       reject(new Error("Failed to load image"));
//     };

//     img.src = url;
//   });
// };

// const resetForm = () => {
//     setStep(0);
//     setIssues([]);
//     setIssuesError("");
//     setErrors({});
//     setUploadingPhotos([]);
//     setPreviewPhoto(null);
//     setRetailerPincode("");
//     setRetailerResults([]);
//     setSelectedRetailer(null);
//     setDefaultRetailer(null);

//     setDraft({
//       device_id: "",
//       issues: [],
//       notes: "",
//       photos: [],
//       service: "pickup",
//       pickup_address_id: addresses?.[0]?.id || "",
//       drop_partner_id: "",
//     });
//   };
 

//   return (
//     <Dialog open={open} onClose={!submitting ? onClose : null} fullWidth 
//     sx={{
//     "& .MuiDialog-paper": {
//       margin: "8px",                 // 👈 reduce outer margin
//       width: "calc(100% - 16px)",    // 👈 prevents overflow
//     },
//   }}>
//       <Box p={3}>
//         {/* HEADER */}
//         <Stack direction="row" justifyContent="space-between" alignItems="center">
//           <Box>
//             <Typography variant="h6" fontWeight={500}>
//               Raise a Claim
//             </Typography>
//             <Typography variant="body2" color="text.secondary">
//               Step {step + 1} of {STEPS.length}
//             </Typography>
//           </Box>
//           <Button
//             disabled={submitting}
//             onClick={() => {
//               resetForm();
//               onClose();
//             }}
//           >
//             Close
//           </Button>

//         </Stack>

//         <Box mt={2}>
//           <Stepper activeStep={step} alternativeLabel>
//             {STEPS.map((s) => (
//               <Step key={s}>
//                 <StepLabel>{s}</StepLabel>
//               </Step>
//             ))}
//           </Stepper>
//         </Box>

//         <Divider sx={{ my: 2 }} />

//         {/* ================= STEP 0 – SELECT DEVICE ================= */}
//         {step === 0 && (
//           <Stack spacing={1.5}>
//             <Typography fontWeight={500}>Select Device</Typography>

//             {normalizedDevices.map((d) => (
//               <Card
//                 key={d.id}
//                 variant="outlined"
//                 sx={{
//                   borderRadius: 2,
//                   cursor: "pointer",
//                   borderColor:
//                     draft.device_id === d.id ? "#4f46e5" : undefined,
//                 }}
//                 onClick={() => handleSelectDevice(d)}
//               >
//                 <CardContent sx={{ position: "relative" }}>
//                   {/* ✅ Selected chip – top right */}
//                   {draft.device_id === d.id && (
//                     <Chip
//                       size="small"
//                       label="Selected"
//                       color="primary"
//                       sx={{
//                         position: "absolute",
//                         top: 8,
//                         right: 8,
//                         fontWeight: 600,
//                       }}
//                     />
//                   )}

//                   <Stack direction="row" spacing={1.5} alignItems="center">
//                     <Avatar sx={{ bgcolor: "#eef2ff" }}>
//                       <PhoneIphoneIcon color="primary" />
//                     </Avatar>

//                     <Box flex={1}>
//                       <Typography fontWeight={500}>
//                         {d.displayPlan}
//                       </Typography>

//                       <Typography variant="caption" color="text.secondary">
//                         {d.displayName}
//                       </Typography>

//                       <Stack spacing={0.3} mt={0.5}>
//                         {d.imei1 && (
//                           <Typography variant="caption" color="text.secondary">
//                             <b>IMEI 1:</b> {d.imei1}
//                           </Typography>
//                         )}

//                         {d.imei2 && (
//                           <Typography variant="caption" color="text.secondary">
//                             <b>IMEI 2:</b> {d.imei2}
//                           </Typography>
//                         )}

//                         {d.serial && (
//                           <Typography variant="caption" color="text.secondary">
//                             <b>Serial:</b> {d.serial}
//                           </Typography>
//                         )}

//                         {d.available_claim && (
//                           <Typography variant="caption" color="text.secondary">
//                             <b>Claims:</b> {d.available_claim}
//                           </Typography>
//                         )}
//                       </Stack>
//                     </Box>
//                   </Stack>
//                 </CardContent>

//               </Card>
//             ))}

//             {errors.device && (
//               <Typography color="error" variant="caption">
//                 {errors.device}
//               </Typography>
//             )}

//           </Stack>
//         )}

//         {/* ================= STEP 1 – ISSUE DETAILS ================= */}
//         {step === 1 && (
//           <Stack spacing={1.5}>
//             <Typography fontWeight={500}>Issue Details</Typography>

//             {issuesLoading && (
//               <Stack direction="row" spacing={1} alignItems="center">
//                 <CircularProgress size={18} />
//                 <Typography variant="caption">Loading issues…</Typography>
//               </Stack>
//             )}

//             {issuesError && (
//               <Typography variant="caption" color="error">
//                 {issuesError}
//               </Typography>
//             )}

//             {!issuesLoading && issues.length === 0 && (
//               <Typography variant="body2" color="text.secondary">
//                 No issues available for this product.
//               </Typography>
//             )}

//             {!issuesLoading && issues.length > 0 && (
//               <FormControl>
//                 <FormLabel>Select Issue(s)</FormLabel>
//                 <FormGroup>
//                   {issues.map((issue) => (
//                     <FormControlLabel
//                       key={issue.id}
//                       label={issue.title}
//                       control={
//                         <Checkbox
//                           checked={draft.issues.some((i) => i.id === issue.id)}
//                           onChange={() => toggleIssue(issue)}
//                         />

//                       }
//                     />
//                   ))}
//                 </FormGroup>
//               </FormControl>
//             )}

//             {errors.issues && (
//               <Typography color="error" variant="caption">
//                 {errors.issues}
//               </Typography>
//             )}


//             <TextField
//               label="Notes (optional)"
//               multiline
//               minRows={3}
//               value={draft.notes}
//               onChange={(e) =>
//                 setDraft((p) => ({ ...p, notes: e.target.value }))
//               }
//             />


//           </Stack>
//         )}

//         {/* ================= STEP 2 – PHOTOS ================= */}
//         {step === 2 && (
//   <Stack spacing={2}>
//     <Typography fontWeight={500}>Upload Photos</Typography>

//     {/* ===== DROP ZONE ===== */}
//     <Box
//       onDragOver={(e) => e.preventDefault()}
//       onDrop={(e) => {
//         e.preventDefault();
//         handleAddPhotos(e.dataTransfer.files);
//       }}
//       sx={{
//         border: "2px dashed #e5e7eb",
//         borderRadius: 2,
//         p: 4,
//         textAlign: "center",
//         bgcolor: "#f9fafb",
//       }}
//     >
//       {/* Upload Icon */}
//       <Box
//         sx={{
//           width: 40,
//           height: 40,
//           borderRadius: "50%",
//           border: "2px solid #3b82f6",
//           mx: "auto",
//           mb: 1,
//           display: "flex",
//           alignItems: "center",
//           justifyContent: "center",
//           color: "#3b82f6",
//           fontSize: 20,
//         }}
//       >
//         ⬆
//       </Box>

//       <Typography fontWeight={600}>Drag your file here</Typography>
//       <Typography variant="caption" color="text.secondary">
//         or
//       </Typography>

//       <Button
//         component="label"
//         sx={{
//           mt: 1,
//           px: 4,
//           borderRadius: "999px",
//           bgcolor: "#2196f3",
//           color: "#fff",
//           textTransform: "none",
//           "&:hover": { bgcolor: "#1e88e5" },
//         }}
//       >
//         Browse
//         <input
//           hidden
//           multiple
//           type="file"
//           accept="image/*"
//           capture="environment"
//           onChange={(e) => handleAddPhotos(e.target.files)}
//         />
//       </Button>
//     </Box>

//     {/* ===== FILE LIST CARD ===== */}
//     {uploadingPhotos.length > 0 && (
//       <Box
//         sx={{
//           bgcolor: "#fff",
//           borderRadius: 2,
//           boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
//           p: 2,
//         }}
//       >
//         <Stack spacing={2}>
//           {uploadingPhotos.map((p) => (
//             <Box key={p.tempId}>
//               {/* File row */}
//               <Stack
//                 direction="row"
//                 justifyContent="space-between"
//                 alignItems="center"
//               >
//                 <Typography fontSize={14}>{p.name}</Typography>

//                 {/* Right icon (visual only) */}
//                 <Box
//                   sx={{
//                     width: 22,
//                     height: 22,
//                     borderRadius: "50%",
//                     border: "1px solid #e5e7eb",
//                     display: "flex",
//                     alignItems: "center",
//                     justifyContent: "center",
//                     fontSize: 12,
//                     color: "#9ca3af",
//                   }}
//                 >
//                   ✕
//                 </Box>
//               </Stack>

//               {/* Progress bar */}
//               <Box
//                 sx={{
//                   mt: 0.8,
//                   height: 5,
//                   borderRadius: 3,
//                   bgcolor: "#e5e7eb",
//                   overflow: "hidden",
//                 }}
//               >
//                 <Box
//                   sx={{
//                     width: `${p.progress || 100}%`,
//                     height: "100%",
//                     bgcolor:
//                       p.status === "success"
//                         ? "#22c55e"
//                         : p.status === "error"
//                         ? "#ef4444"
//                         : "#facc15",
//                   }}
//                 />
//               </Box>

//               {/* Status */}
//               <Typography
//                 variant="caption"
//                 sx={{ color: "#9ca3af", mt: 0.3 }}
//               >
//                 {p.status === "uploading" && "Uploading"}
//                 {p.status === "success" && "Completed"}
//                 {p.status === "error" && "Failed"}
//               </Typography>
//             </Box>
//           ))}
//         </Stack>
//       </Box>
//     )}

//     {errors.photos && (
//       <Typography color="error" variant="caption">
//         {errors.photos}
//       </Typography>
//     )}
//   </Stack>
// )}


//         {/* ================= STEP 3 – LOCATION ================= */}
//         {step === 3 && (
//           <Stack spacing={2}>
//             <Typography fontWeight={500}>Service Method</Typography>

//             <RadioGroup
//               row
//               value={draft.service}
//               onChange={(e) =>
//                 setDraft((p) => ({ ...p, service: e.target.value }))
//               }
//             >
//               <FormControlLabel value="pickup" control={<Radio />} label="Pickup" />
//               <FormControlLabel value="drop" control={<Radio />} label="Drop-off" />
//             </RadioGroup>

//             {/* ================= PICKUP ================= */}
//             {draft.service === "pickup" &&
//               addresses.map((a) => (
//                 <Card
//                   key={a.id}
//                   variant="outlined"
//                   sx={{
//                     borderRadius: 2,
//                     cursor: "pointer",
//                     borderColor:
//                       draft.pickup_address_id === a.id ? "#4f46e5" : undefined,
//                   }}
//                   onClick={() =>
//                     setDraft((p) => ({ ...p, pickup_address_id: a.id }))
//                   }
//                 >
//                   <CardContent>
//                     <Typography fontWeight={500}>{a.name}</Typography>
//                     <Typography variant="caption">
//                       {[a.address1, a.city, a.state, a.pincode]
//                         .filter(Boolean)
//                         .join(", ")}
//                     </Typography>
//                   </CardContent>
//                 </Card>
//               ))}

//             {/* ================= DROP-OFF ================= */}
//             {draft.service === "drop" && (
//               <>
//                 {/* DEFAULT RETAILER */}
//                 {defaultRetailer && (
//                   <Card
//                     variant="outlined"
//                     sx={{
//                       borderRadius: 2,
//                       cursor: "pointer",
//                       borderColor:
//                         activeRetailer?.id === defaultRetailer.id
//                           ? "#4f46e5"
//                           : "#e5e7eb",
//                       backgroundColor:
//                         activeRetailer?.id === defaultRetailer.id
//                           ? "#eef2ff"
//                           : "transparent",
//                     }}
//                     onClick={() => {
//                       setSelectedRetailer(null); // 👈 THIS IS THE KEY LINE
//                     }}
//                   >
//                     <CardContent>
//                       <Typography fontWeight={500}>
//                         {defaultRetailer.business_name}
//                       </Typography>

//                       <Typography variant="caption">
//                         {[
//                           defaultRetailer.address_line1,
//                           defaultRetailer.city,
//                           defaultRetailer.state,
//                           defaultRetailer.pincode,
//                         ]
//                           .filter(Boolean)
//                           .join(", ")}
//                       </Typography>

//                       {activeRetailer?.id === defaultRetailer.id && (
//                         <Chip
//                           size="small"
//                           color="primary"
//                           label="Selected"
//                           sx={{ mt: 1 }}
//                         />
//                       )}
//                     </CardContent>
//                   </Card>
//                 )}

//                 <Divider />

//                 {/* SEARCH BY PINCODE */}
//                 <Typography fontWeight={400}>
//                   Select another retailer
//                 </Typography>

//                 <Stack direction="row" spacing={1}>
//                   <TextField
//                     label="Enter Pincode"
//                     value={retailerPincode}
//                     onChange={(e) => setRetailerPincode(e.target.value)}
//                     inputProps={{ maxLength: 6 }}
//                     size="small"
//                   />
//                   <Button
//                     variant="outlined"
//                     onClick={fetchRetailersByPincode}
//                     disabled={retailerPincode.length !== 6 || retailerLoading}
//                   >
//                     Search
//                   </Button>
//                 </Stack>

//                 {retailerLoading && <CircularProgress size={18} />}

//                 {retailerResults.map((r) => (
//                   <Card
//                     key={r.id}
//                     variant="outlined"
//                     sx={{
//                       borderRadius: 2,
//                       cursor: "pointer",
//                       borderColor:
//                         activeRetailer?.id === r.id ? "#16a34a" : "#e5e7eb",
//                       backgroundColor:
//                         activeRetailer?.id === r.id ? "#ecfdf5" : "transparent",
//                     }}
//                     onClick={() => setSelectedRetailer(r)}
//                   >
//                     <CardContent>
//                       <Typography fontWeight={500}>{r.business_name}</Typography>
//                       <Typography variant="caption">
//                         {[r.address_line1, r.city, r.state, r.pincode]
//                           .filter(Boolean)
//                           .join(", ")}
//                       </Typography>

//                       {activeRetailer?.id === r.id && (
//                         <Chip
//                           size="small"
//                           color="success"
//                           label="Selected"
//                           sx={{ mt: 1 }}
//                         />
//                       )}
//                     </CardContent>
//                   </Card>
//                 ))}
//               </>
//             )}

//             {errors.location && (
//               <Typography color="error" variant="caption">
//                 {errors.location}
//               </Typography>
//             )}

//           </Stack>
//         )}

//         {/* ================= STEP 4 – REVIEW ================= */}

//         {step === 4 && (
//           <Stack spacing={2}>
//             <Typography fontWeight={500}>Review Your Claim</Typography>

//             {/* DEVICE */}
//             <Card variant="outlined">
//               <CardContent>
//                 <Typography fontWeight={400}>Device</Typography>
//                 <Typography variant="body2">
//                   {selectedDevice?.displayPlan}
//                 </Typography>
//                 <Typography variant="caption" color="text.secondary">
//                   {selectedDevice?.displayName}
//                 </Typography>
//               </CardContent>
//             </Card>

//             {/* ISSUES */}
//             <Card variant="outlined">
//               <CardContent>
//                 <Typography fontWeight={400}>Issues</Typography>
//                 <Stack direction="row" spacing={1} flexWrap="wrap">
//                   {draft.issues.map((i) => (
//                     <Chip key={i.id} label={i.title} size="small" />
//                   ))}
//                 </Stack>


//                 {draft.notes && (
//                   <>
//                     <Divider sx={{ my: 1 }} />
//                     <Typography variant="caption" color="text.secondary">
//                       Notes
//                     </Typography>
//                     <Typography variant="body2">{draft.notes}</Typography>
//                   </>
//                 )}
//               </CardContent>
//             </Card>

//             {/* SERVICE METHOD */}
//             <Card variant="outlined">
//               <CardContent>
//                 <Typography fontWeight={400}>Service Method</Typography>
//                 <Typography variant="body2">
//                   {draft.service === "pickup" ? "Pickup from Address" : "Drop-off at Retailer"}
//                 </Typography>

//                 <Divider sx={{ my: 1 }} />

//                 {/* PICKUP ADDRESS */}
//                 {draft.service === "pickup" && pickupAddress && (
//                   <>
//                     <Typography fontWeight={600}>Pickup Address</Typography>
//                     <Typography variant="caption">
//                       {[pickupAddress.address1,
//                         pickupAddress.city,
//                         pickupAddress.state,
//                         pickupAddress.pincode]
//                         .filter(Boolean)
//                         .join(", ")}
//                     </Typography>
//                   </>
//                 )}

//                 {/* DROP RETAILER */}
//                 {draft.service === "drop" && activeRetailer && (
//                   <>
//                     <Typography fontWeight={600}>Drop-off Retailer</Typography>
//                     <Typography variant="caption">
//                       {[activeRetailer.address_line1,
//                         activeRetailer.city,
//                         activeRetailer.state,
//                         activeRetailer.pincode]
//                         .filter(Boolean)
//                         .join(", ")}
//                     </Typography>
//                   </>
//                 )}
//               </CardContent>
//             </Card>

//             {/* PHOTOS */}
//             <Card variant="outlined">
//               <CardContent>
//                 <Typography fontWeight={400}>
//                   Uploaded Photos ({draft.photos.length})
//                 </Typography>

//                 {draft.photos.length === 0 ? (
//                   <Typography variant="caption" color="text.secondary">
//                     No photos uploaded
//                   </Typography>
//                 ) : (
//                   <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
//                     {draft.photos.map((p, i) => (
//                       <Box
//                         key={i}
//                         component="img"
//                         src={p.photo_url}
//                         alt={`photo-${i}`}
//                         sx={{
//                           width: 70,
//                           height: 70,
//                           objectFit: "cover",
//                           borderRadius: 1,
//                           border: "1px solid #e5e7eb",
//                           cursor: "pointer",
//                         }}
//                         onClick={() => setPreviewPhoto(p.photo_url)}
//                       />
//                     ))}
//                   </Stack>
//                 )}
//               </CardContent>
//             </Card>
//           </Stack>
//         )}


//         <Divider sx={{ my: 2 }} />

//         {/* ACTIONS */}
//         <Stack direction="row" spacing={1}>
//           <Button
//             fullWidth
//             variant="outlined"
//             disabled={step === 0 || submitting}
//             onClick={back}
//           >
//             Back
//           </Button>

//           {step < 4 ? (
//             <Button
//               fullWidth
//               variant="contained"
//               disabled={
//                 (step === 0 && !draft.device_id) ||
//                 (step === 1 && draft.issues.length === 0)
//               }
//               onClick={handleNext}
//             >
//               Next
//             </Button>
//           ) : (
//             <Button
//               fullWidth
//               variant="contained"
//               disabled={submitting}
//               onClick={raiseClaim}
//             >
//               Submit Claim
//             </Button>
//           )}
//         </Stack>
//       </Box>

//       {/* PHOTO PREVIEW DIALOG */}
//       <Dialog
//         open={Boolean(previewPhoto)}
//         onClose={() => setPreviewPhoto(null)}
//         maxWidth="sm"
//         fullWidth
//       >
//         <Box p={2}>
//           <Stack
//             direction="row"
//             justifyContent="space-between"
//             alignItems="center"
//             mb={1}
//           >
//             <Typography fontWeight={400}>Photo Preview</Typography>
//             <Button onClick={() => setPreviewPhoto(null)}>Close</Button>
//           </Stack>

//           {previewPhoto && (
//             <Box
//               component="img"
//               src={previewPhoto}
//               alt="Uploaded Preview"
//               sx={{
//                 width: "100%",
//                 maxHeight: "70vh",
//                 objectFit: "contain",
//                 borderRadius: 1,
//                 border: "1px solid #e5e7eb",
//               }}
//             />
//           )}
//         </Box>
//       </Dialog>

//     </Dialog>
//   );
// }